import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Search, Pencil, ChevronDown, ChevronRight, Send, Building2, UserRound, FileText } from 'lucide-react';
import './CommercialEntityDetailPage.css';
import '../../../shared/StyledInput.css';
import PageHeader from '../../../shared/PageHeader';
import BrowseNav, { useBrowseIds } from '../../../shared/BrowseNav';
import Accordion from '../../../shared/Accordion';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import InlineEditField from '../../../shared/InlineEditField';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import ExternalLink from '../../../shared/ExternalLink';
import RecordChip from '../../../shared/RecordChip';
import FieldHelp from '../../../shared/FieldHelp';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { Pill, CategoryPill, GoogleLookupPill } from '../../../shared/Pill';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { thumbFileUrl } from '../../../shared/mediaFileUrl';
import { formatPrice } from '../../sales/Prices/priceLookupShared';
import AddLinkDrawer from './AddLinkDrawer';
import AddTradeShowAttendanceDrawer from './AddTradeShowAttendanceDrawer';
import OpeningHoursCard from './OpeningHoursCard';
import CustomerLoginsCard from './CustomerLoginsCard';
import AccountApplicationsCard from './AccountApplicationsCard';
import BusinessNumberCheckDetails from './BusinessNumberCheckDetails';
import CustomerQuickBooksCard from './CustomerQuickBooksCard';
import OwnedBrandsCard from './OwnedBrandsCard';
import LogActivityDrawer from './LogActivityDrawer';
import ComposeEmailDrawer from './ComposeEmailDrawer';
import PullEmailsDialog from './PullEmailsDialog';
import MoveActivityDrawer from './MoveActivityDrawer';
import RichText, { htmlToPreviewText, cleanEmailPreviewText } from '../../../shared/RichText';
import TrafficLightDot from '../../../shared/TrafficLightDot';
import { ACTIVITY_TYPES, LOGGABLE_ACTIVITY_TYPES, SYSTEM_ACTIVITY_TYPE, ACTIVITY_TYPE_LABELS, ACTIVITY_TYPE_ICONS, DIRECTION_LABELS, CHANNEL_LABELS } from './activityConstants';
import { COUNTRIES } from '../../../shared/countries';
import { addressLines, addressParts, addressWarnings, findRegion, partsDroppedFor, capitaliseWords } from '../../../shared/addressFormat';
import { AddressCheckPill, AddressFieldWarning, PostcodeCheckPill, RegionSelect, SuburbInput } from '../../../shared/AddressFields';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import { BUSINESS_NUMBER_TYPES, businessNumberLabel, businessNumberTypeForCountry, formatBusinessNumber, spaceAbnInput } from '../../../shared/businessNumber';
import DateInput from '../../../shared/DateInput';
import CommercialTermsPrompt, { hasCommercialTerms } from './CommercialTermsPrompt';
import ConvertToCustomerDialog from './ConvertToCustomerDialog';
import CloseProspectDialog from './CloseProspectDialog';
import ReopenProspectDialog from './ReopenProspectDialog';
import { formatMoney, formatNumber, getTenantHomeCurrency } from '../../../shared/numberFormat';
import { useClassificationOptions, ClassificationField, marketAddOptions, marketRowFor, marketRowText, heldAddOptions } from '../../../shared/OrganisationClassification';
import { tickedCardKeys } from '../../../shared/entityCards';
import { SALES_ORDER_STATUS_LABELS } from '../../../shared/salesOrderStatus';
import { carrierChoices, servicesForCarrier } from '../../../shared/shippingCarriage';
import { askConfirm } from '../../../shared/AppDialogs';

const TYPE_LABELS = { organization: 'Organisation', individual: 'Individual' };

// Who or what added the record (issue #286).
function addedByLabel(entity) {
  if (entity.created_by_source === 'trade_enquiry') return 'Website Trade Enquiry';
  return entity.created_by_user?.name || 'Unknown';
}
const QUOTE_STATUS_LABELS = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', rejected: 'Rejected', cancelled: 'Cancelled', expired: 'Expired', withdrawn: 'Withdrawn' };

// Mirrors the type coercion CommercialEntityForm's handleSubmit does before
// sending its full-save payload. The backend's shared resolveEntityFields
// (used by both the full Save and this per-field Save) intentionally
// preserves false/0/'' as real overrides for the Tier-cascade fields
// (Payment Term, Credit Limit, Tax Handling Type — null there means
// "follow the Tier"), so a cleared draft has to become an actual `null`
// here rather than an empty string reaching the server.
function preparePatchValue(field, raw) {
  if (field === 'credit_limit') return raw === '' || raw === null ? null : Number(raw);
  if (['payment_term_id', 'tax_handling_type_id'].includes(field)) {
    return raw === '' || raw === null ? null : raw;
  }
  return raw;
}

const panelStyle = { background: '#fff', border: '1px solid #b3bbc7', borderRadius: 8 };
const emptyNoteStyle = { fontSize: 13, color: '#999' };
const childRowStyle = { position: 'relative', marginBottom: 14, paddingBottom: 12, paddingRight: 22, borderBottom: '1px solid #eef0f3' };
const removeRowBtnStyle = { position: 'absolute', top: 0, right: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 15, lineHeight: 1 };
// An Activity row's ⋮ menu (Create Task / Edit / Move Activity / Delete).
const activityMenuStyle = { position: 'absolute', top: -4, right: -6 };
const linkDirectionStyle = { display: 'inline-block', width: 16, color: '#9aa5b5', fontWeight: 400 };
const draftFieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const quickActionBtnStyle = { width: 39, height: 39, borderRadius: '50%', border: '1px solid #d7dbe3', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', cursor: 'pointer' };
const quickActionLabelStyle = { fontSize: 12, color: '#333', marginTop: 6 };
// Link-style actions under a Relationship row's Stage (Won, Close, Reopen).
const relationshipActionStyle = { background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 };

// One line for a portal delivery address request's address.
// In its country's order (#395); a request from before then was Australian.
const addressRequestLine = (addr) => {
  const country = addr.country || 'Australia';
  return [...addressLines({ ...addr, country }), country].join(', ');
};

// Log-an-Activity shortcuts — HubSpot-style icon row above the Activities
// tabs (story #216). Each opens LogActivityDrawer pre-set to that Activity
// Type. System isn't one — the app records those itself.
const QUICK_ACTIONS = LOGGABLE_ACTIVITY_TYPES.map((type) => ({ type, label: ACTIVITY_TYPE_LABELS[type], Icon: ACTIVITY_TYPE_ICONS[type] }));

// Pricelists (issue #241) isn't an Activity Type — it narrows to Emails
// that carried a Pricelist-template send, however the template's been
// renamed since.
const PRICELISTS_TAB = 'Pricelists';
const ACTIVITY_TABS = ['All activities', ...ACTIVITY_TYPES.map((t) => ACTIVITY_TYPE_LABELS[t]), PRICELISTS_TAB];
const activityMatchesTab = (a, tab) => {
  if (tab === 'All activities') return true;
  if (tab === PRICELISTS_TAB) return (a.pricelist_sends || []).length > 0;
  return ACTIVITY_TYPE_LABELS[a.activity_type] === tab;
};
// "QO-00012" — same format as documentNumberService.js's formatDocumentNumber.
const quoteNumberOf = (q) => `${q.document_type_code}-${String(q.number).padStart(5, '0')}`;
// "Dealer · SP Connectors · Each" — the Pricelist's scope as it stood when sent.
const pricelistScopeLabel = (ps) => [
  ps.customer_price_group?.name,
  ps.product_price_group?.name || 'All products',
  ps.uom?.name,
].filter(Boolean).join(' · ');

// Prospect and Customer are separate Relationship Types, and a record holds
// at most one of them (vis_relationship_lifecycle.md §6).
const LIFECYCLE_TYPE_KEYS = ['prospect', 'customer'];
const holdsType = (entity, systemKey) => (entity?.relationships || []).some((r) => r.relationship_type?.system_key === systemKey);

// An entity holding the Supplier Relationship Type — checked straight off
// its own held relationships (which already carry relationship_type via
// ENTITY_INCLUDE).
function isSupplierEntity(entity) {
  return holdsType(entity, 'supplier');
}

// An entity holding a Prospect or Customer relationship — such a record
// can't add the other of the two. Which cards show is the Entity Cards
// grid's job (shared/entityCards.js), not this.
function isCustomerEntity(entity) {
  return LIFECYCLE_TYPE_KEYS.some((key) => holdsType(entity, key));
}

// Picks which list "Back" returns to, without needing the caller to pass
// that down as navigation state. Prospect/Customer is checked before
// Supplier, since a record can hold both.
// Prefers the list screen actually navigated from (passed as router state,
// see the four list screens' own navigate() calls) — falls back to guessing
// from the entity's own relationships only when there's no such state (a
// direct link/refresh, or a link from within a card that isn't a list row,
// e.g. People/Companies or Trade Show Attendees). Falls all the way through
// to the generic All Entities screen for an entity with no Customer/Supplier
// relationship at all, rather than guessing a specific list it won't
// actually appear on.
// A link to an outside site in a click-to-edit field: the link, then the
// pencil marking the field as editable (ui-architecture.md "External Link").
function externalLinkDisplay(v, label) {
  if (!v) return '—';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0, maxWidth: '100%' }}>
      <ExternalLink href={v} label={label} />
      <Pencil size={12} color="#9aa5b5" style={{ marginLeft: 4, flexShrink: 0 }} />
    </span>
  );
}

function backCrumb(entity, origin) {
  if (origin) return origin;
  if (holdsType(entity, 'customer')) return { label: 'Customers', path: '/customer-customers' };
  if (holdsType(entity, 'prospect')) return { label: 'Prospects', path: '/crm-leads?lifecycle=prospects' };
  if (isSupplierEntity(entity)) return { label: 'Suppliers', path: '/suppliers' };
  return { label: 'Entities', path: '/market-intel-all-entities' };
}

// Australia Post's Suburbs for an Australian Postcode, as it's typed (#158).
async function fetchPostcodeCheck(query) {
  const res = await axios.get('/api/postcode-check', { params: query });
  return res.data.result;
}

// Click-to-edit for an Australian Address's Suburb, State and Postcode,
// saved together so Australia Post checks them as a whole (#158). The
// Suburb offers Australia Post's Suburbs for the Postcode and warns when
// it isn't one of them; Save always saves. `checkPill` shows Australia
// Post's check beside the label, whatever the Suburb's length.
function AuLocalityInlineField({ address, onSave, checkPill }) {
  const { canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ suburb: '', state: '', postcode: '' });
  const [saving, setSaving] = useState(false);

  const display = [address.suburb, address.state, address.postcode].filter(Boolean).join(' ') || '—';
  const states = addressParts('Australia').find((p) => p.key === 'state')?.options || [];

  const startEditing = () => {
    setDraft({ suburb: address.suburb || '', state: address.state || '', postcode: address.postcode || '' });
    setEditing(true);
  };
  const change = (fields) => setDraft((d) => ({ ...d, ...fields }));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ suburb: capitaliseWords(draft.suburb.trim()), state: draft.state, postcode: draft.postcode.trim() });
      dismissError();
      setEditing(false);
    } catch (err) {
      showError(err, 'Update failed');
    }
    setSaving(false);
  };

  if (!editing) {
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ ...draftFieldLabelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
          Suburb, State, Postcode
          <span style={{ textTransform: 'none', letterSpacing: 0 }}>{checkPill}</span>
        </div>
        <div
          className={canUpdate ? 'inline-edit-field' : undefined}
          onClick={() => { if (canUpdate) startEditing(); }}
          title={canUpdate ? 'Click to edit' : undefined}
          style={{ fontSize: 15, color: '#222', wordBreak: 'break-word', cursor: canUpdate ? 'pointer' : 'default', padding: '2px 4px', margin: '-3px -5px', borderRadius: 4 }}
        >
          {display}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 10 }} onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') { e.preventDefault(); save(); } }}>
      <div style={draftFieldLabelStyle}>Suburb, State, Postcode</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '2 1 140px' }}>
          <SuburbInput
            address={{ ...draft, country: 'Australia' }}
            fetchCheck={fetchPostcodeCheck}
            onChange={change}
            inputProps={{ className: 'styled-input', autoFocus: true, placeholder: 'Suburb', 'aria-label': 'Suburb', style: { width: '100%' } }}
          />
        </div>
        <RegionSelect country="Australia" value={draft.state} options={states} onChange={(v) => change({ state: v })} ariaLabel="State" style={{ flex: '1 1 110px', width: 'auto' }} />
        <input className="styled-input" value={draft.postcode} onChange={(e) => change({ postcode: e.target.value })} placeholder="Postcode" aria-label="Postcode" inputMode="numeric" style={{ flex: '0 1 90px' }} />
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
        <SaveButton loading={saving} onClick={save} type="button" />
        <CancelButton onClick={() => setEditing(false)} disabled={saving} />
      </div>
    </div>
  );
}

// One link row's ended date, shown once a link has been ended. Click-to-
// edit like InlineEditField, but inline within the row rather than its own
// labelled block — lets a mistaken "today" get corrected to the real date
// after the fact.
// Click-to-edit Link Role — same pattern as EndedAtLabel below, but a
// dropdown instead of a date input. `options` is already filtered to the
// category matching the *other* entity's type (mirrors AddLinkDrawer and
// the backend's own category-match validation), so every choice here is
// guaranteed valid to save.
function LinkRoleLabel({ value, roleName, options, onSave }) {
  const { canUpdate } = useScreenAccess();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value || ''));

  if (!canUpdate) return <CategoryPill value={roleName} />;
  if (!editing) {
    return (
      <span onClick={(e) => { e.stopPropagation(); setDraft(String(value || '')); setEditing(true); }} title="Click to change role" style={{ cursor: 'pointer' }}>
        <CategoryPill value={roleName} />
      </span>
    );
  }
  return (
    <select
      autoFocus
      value={draft}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={async () => { if (draft && draft !== String(value)) await onSave(draft); setEditing(false); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(false); }}
      style={{ fontSize: 12 }}
    >
      {options.map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
    </select>
  );
}

function EndedAtLabel({ value, onSave }) {
  const { canUpdate } = useScreenAccess();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  if (!canUpdate) return <span style={{ fontSize: 12, color: '#999' }}>Ended {formatDate(value)}</span>;
  if (!editing) {
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setDraft(value || ''); setEditing(true); }}
        title="Click to edit date"
        style={{ fontSize: 12, color: '#999', cursor: 'pointer' }}
      >
        Ended {formatDate(value)}
      </span>
    );
  }
  return (
    <DateInput
      autoFocus
      value={draft}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={async () => { await onSave(draft); setEditing(false); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditing(false); }}
      style={{ fontSize: 12 }}
    />
  );
}

// Body of the People/Companies cards — current links first, ended ones
// tucked under a collapsed "Past" toggle so a role's history doesn't
// crowd out who currently holds it. Shared by both cards: the row shape
// (direction, link_role, related_entity/commercial_entity) is identical,
// only the category filter upstream differs.
function LinkRows({ items, emptyLabel, navigate, onRemove, onEnd, onUndoEnd, onEditEndedAt, linkRoleTypes, onEditRole }) {
  const { canUpdate } = useScreenAccess();
  const [showPast, setShowPast] = useState(false);
  // Most important (lowest Link Role priority) first — same ranking
  // CustomerPipelinePage's card contact pick uses, so the two never
  // disagree about who matters most. Roles with no priority (Organisation-
  // category, or none set) sort after every ranked one.
  const byPriority = (a, b) => (a.link_role?.priority ?? Infinity) - (b.link_role?.priority ?? Infinity);
  const current = items.filter((r) => !r.ended_at).sort(byPriority);
  const past = items.filter((r) => r.ended_at);

  const row = (r) => {
    const other = r.direction === 'out' ? r.related_entity : r.commercial_entity;
    const isPast = !!r.ended_at;
    return (
      <div key={`${r.direction}-${r.id}`} style={{ ...childRowStyle, cursor: 'pointer', opacity: isPast ? 0.6 : 1 }} onClick={() => navigate(`/commercial-entities/${other.id}`)}>
        {canUpdate && r.direction === 'out' && (
          <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center', gap: 8 }} onClick={(e) => e.stopPropagation()}>
            {isPast ? (
              <button type="button" onClick={() => onUndoEnd(r.id)} title="Undo end" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1976d2', fontSize: 12 }}>Undo</button>
            ) : (
              <button type="button" onClick={() => onEnd(r.id)} title="End this link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5a6b85', fontSize: 12 }}>End</button>
            )}
            <button type="button" onClick={() => onRemove(r.id, other.name)} title="Remove" style={{ ...removeRowBtnStyle, position: 'static' }}>×</button>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1976d2' }}>
            <span style={linkDirectionStyle} title={r.direction === 'out' ? 'Outgoing link' : 'Incoming link'}>{r.direction === 'out' ? '→' : '←'}</span>
            {other.name}
          </div>
          {r.direction === 'out' ? (
            <LinkRoleLabel
              value={r.link_role_type_id}
              roleName={r.link_role?.name}
              options={linkRoleTypes.filter((t) => t.category === other.type)}
              onSave={(v) => onEditRole(r.id, v)}
            />
          ) : (
            <span title={`Edit this role from ${other.name}'s own page`}>
              <CategoryPill value={r.link_role?.inverse_name || r.link_role?.name} />
            </span>
          )}
        </div>
        {isPast && (
          <div style={{ marginTop: -8 }}>
            {r.direction === 'out'
              ? <EndedAtLabel value={r.ended_at} onSave={(date) => onEditEndedAt(r.id, date)} />
              : <span style={{ fontSize: 12, color: '#999' }}>Ended {formatDate(r.ended_at)}</span>
            }
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {current.length ? current.map(row) : (past.length === 0 && <div style={emptyNoteStyle}>{emptyLabel}</div>)}
      {past.length > 0 && (
        <div style={{ marginTop: current.length ? 4 : 0 }}>
          <button
            type="button"
            onClick={() => setShowPast((v) => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5a6b85', fontSize: 13, padding: 0, marginBottom: showPast ? 10 : 0 }}
          >
            Past ({past.length}) {showPast ? '▲' : '▼'}
          </button>
          {showPast && past.map(row)}
        </div>
      )}
    </>
  );
}

// Click-to-edit-and-auto-save record display (left panel) + a Links panel
// (right) + a placeholder for the future Activities feature (middle).
// Every field, including the Address/Email/Phone lists, is editable inline
// here — there's no standalone Edit action; `type` (Organisation/Individual)
// is fixed once the record is created.
function CommercialEntityDetailPage() {
  const { canCreate, canUpdate } = useScreenAccess();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const browseIds = useBrowseIds();
  const showError = useErrorToast();
  const dismissError = useDismissError();

  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  // Currency and today's rate for the Supplier-only Currency card — fetched
  // separately from `entity` since ENTITY_INCLUDE (the generic detail
  // fetch) doesn't carry them, and /api/suppliers/:id already builds
  // exactly this shape (supplierController.getSupplier).
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [activeActivityTab, setActiveActivityTab] = useState('All activities');
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  // Which Activity row is expanded inline (one at a time, same accordion
  // shape as AllEntitiesTable.js's +N relationships) — a click reads the
  // full record (notes/email body, direction, channel) in place, using the
  // middle column's own width instead of a separate drawer; editing still
  // goes through LogActivityDrawer via the row's own pencil icon.
  const [expandedActivityId, setExpandedActivityId] = useState(null);
  // Drawer is shared by both "log new" (quick-action icons, drawerType set,
  // editingActivity null) and "edit" (row click, both set from the existing
  // row) — see LogActivityDrawer's own header comment. editingActivityEntity
  // is usually just `entity`, except when editing a rolled-up row (an
  // Activity that actually belongs to a linked contact, not this
  // Organisation itself — see the rollup comment on the Activities list
  // fetch below) — the drawer needs that row's real owner, not whichever
  // entity's page it happens to be displayed on.
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [pullEmailsOpen, setPullEmailsOpen] = useState(false);
  const [drawerActivityType, setDrawerActivityType] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingActivityEntity, setEditingActivityEntity] = useState(null);
  // Set only when the drawer was opened via an existing row's "Create
  // follow-up Task" action (issue #218) — the Activity a new Task should
  // link back to. Null for a standalone Task (quick-action icon) and for
  // every other Activity Type.
  const [followUpFromActivity, setFollowUpFromActivity] = useState(null);
  // The row (if any) currently in MoveActivityDrawer, reassigning which
  // Organisation/Person the Activity is logged against.
  const [movingActivity, setMovingActivity] = useState(null);
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [linkRoleTypes, setLinkRoleTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const { markets: marketOptions, segments: segmentOptions, channels: channelOptions, operators: operatorOptions } = useClassificationOptions();
  const [savingMarkets, setSavingMarkets] = useState(false);
  const [savingChannels, setSavingChannels] = useState(false);
  const [savingOperators, setSavingOperators] = useState(false);
  // Classification card — values read as text; the kebab adds one (reveals
  // that field's select) or switches the lists into edit mode (☆ / ×).
  const [classificationOpen, setClassificationOpen] = useState(false);
  const [classificationEditing, setClassificationEditing] = useState(false);
  const [classificationAdding, setClassificationAdding] = useState(null);
  // Legal card — Legal Entity, Business Number and what its register (ABN
  // Lookup for an ABN) holds for it; its kebab re-checks the number.
  const [legalOpen, setLegalOpen] = useState(false);
  const [checkingNumber, setCheckingNumber] = useState(false);
  const [businessRegisters, setBusinessRegisters] = useState([]);
  // Operators card — same read-first, edit-from-the-kebab shape.
  const [operatorsOpen, setOperatorsOpen] = useState(false);
  const [operatorsEditing, setOperatorsEditing] = useState(false);
  const [operatorsAdding, setOperatorsAdding] = useState(false);
  const [legalEntityTypes, setLegalEntityTypes] = useState([]);
  const [customerTiers, setCustomerTiers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [customerPriceGroups, setCustomerPriceGroups] = useState([]);
  const [productPriceGroups, setProductPriceGroups] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [taxHandlingTypes, setTaxHandlingTypes] = useState([]);
  const [shippingServices, setShippingServices] = useState([]);
  const [optionsForService, setOptionsForService] = useState([]);
  const [addressTypes, setAddressTypes] = useState([]);
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [addressLookingUp, setAddressLookingUp] = useState(false);
  const [emailTypes, setEmailTypes] = useState([]);

  // Add Person/Add Company both open the same drawer, parameterized by
  // which category of link it's creating.
  const [addLinkCategory, setAddLinkCategory] = useState(null);
  const [showAddAttendance, setShowAddAttendance] = useState(false);

  // Emails/Phones can't be created blank like an Address (email/phone_number
  // are NOT NULL — see their models) — a draft row holds the new value
  // locally until it has real content, then POSTs and clears itself, same
  // shape as QuotePage.js's always-blank bottom row for lines.
  const [emailDraft, setEmailDraft] = useState({ email_type_id: '', email: '' });
  const [phoneDraft, setPhoneDraft] = useState({ phone_type_id: '', phone_number: '' });
  // Draft form visibility — hidden until "Add Email"/"Add Phone" is picked
  // from the card's kebab, unlike Addresses (addAddress creates the row
  // server-side immediately, no local draft to reveal).
  const [showEmailDraft, setShowEmailDraft] = useState(false);
  const [showPhoneDraft, setShowPhoneDraft] = useState(false);

  // Addresses/Emails/Phones accordions default collapsed — "Add" from the
  // kebab menu should still reveal the new/draft row it just created, so
  // each accordion's open state is lifted here (controlled) instead of
  // living inside Accordion, letting the Add handlers force it open.
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [emailsOpen, setEmailsOpen] = useState(false);
  const [phonesOpen, setPhonesOpen] = useState(false);

  // Price Groups (issue #240) — same kebab "Add" + local draft shape as
  // Phones; the draft POSTs once both of its selects are filled in.
  const [priceGroupsOpen, setPriceGroupsOpen] = useState(false);
  const [showPriceGroupDraft, setShowPriceGroupDraft] = useState(false);
  const [priceGroupDraft, setPriceGroupDraft] = useState({ product_price_group_id: '', customer_price_group_id: '' });

  // Website's own "re-run the URL-lookup fetch" action, plus the
  // Logo box's manual upload — both act right away (independent of any
  // Save button, same "acts immediately" idiom as every InlineEditField on
  // this page) so they get their own loading/warning state rather than
  // reusing patchField's.
  const [refreshingWebsite, setRefreshingWebsite] = useState(false);
  const [webFetchWarnings, setWebFetchWarnings] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchEntity = useCallback(() => {
    setLoading(true);
    // pipeline_dot: 1 also resolves which logged Activity is driving this
    // entity's Pipeline dot (story #227 follow-up), so the Activities feed
    // below can mark that one row instead of leaving the board as the only
    // place the reason shows up.
    axios.get(`/api/commercial-entities/${id}`, { params: { pipeline_dot: true } })
      .then((res) => setEntity(res.data.data))
      .catch((err) => { showError(err, 'Could not load record'); setEntity(null); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => { fetchEntity(); }, [fetchEntity]);

  useEffect(() => {
    if (!isSupplierEntity(entity)) { setSupplierInfo(null); return; }
    axios.get(`/api/suppliers/${id}`)
      .then((res) => setSupplierInfo(res.data.data))
      .catch(() => setSupplierInfo(null));
  }, [entity, id]);

  useEffect(() => {
    axios.get('/api/quotes', { params: { customer_id: id, limit: 10 } })
      .then((res) => setQuotes(res.data.data || []))
      .catch(() => setQuotes([]));
    axios.get('/api/sales-orders', { params: { customer_id: id, limit: 10 } })
      .then((res) => setSalesOrders(res.data.data || []))
      .catch(() => setSalesOrders([]));
  }, [id]);

  // Who can be picked as Account Owner (issue #340).
  const [accountOwners, setAccountOwners] = useState([]);
  useEffect(() => {
    axios.get('/api/users/account-owners')
      .then((res) => setAccountOwners(res.data.data || []))
      .catch(() => setAccountOwners([]));
  }, []);

  // Activities — fetched as its own resource (not part of ENTITY_INCLUDE,
  // see activityController.js's header comment), on mount. Fetched
  // unfiltered; the tab row below filters client-side rather than
  // re-fetching per tab, since the whole log is already a small, single
  // round-trip. For an Organisation, the backend also rolls in Activities
  // logged directly against its linked Individual contacts (see
  // activityController.js's resolveActivityEntityIds) — each row's own
  // `commercial_entity` says which Entity it actually belongs to, so a
  // rolled-up row can be told apart from one logged against this Entity
  // itself.
  const fetchActivities = useCallback(() => {
    setActivitiesLoading(true);
    // limit raised past the endpoint's own 100 default — Email Sync alone
    // can put an Entity well past that (see the scrollable wrapper below,
    // which is what actually keeps a long list usable, not this cap).
    axios.get('/api/activities', { params: { commercial_entity_id: id, limit: 300 } })
      .then((res) => setActivities(res.data.data || []))
      .catch(() => setActivities([]))
      .finally(() => setActivitiesLoading(false));
  }, [id]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  // Arriving here from a Home Dashboard Calendar item click (issue #229) —
  // land on the Activities tab with that specific row expanded, same as the
  // existing in-page "jump to a row" actions (followed_from_activity/
  // follow_up_tasks clicks below). Clears the consumed state afterward so a
  // refresh doesn't keep re-expanding it. An emailed link (a portal
  // delivery address request's staff alert) carries it as ?activity=ID.
  useEffect(() => {
    const fromQuery = Number(new URLSearchParams(location.search).get('activity')) || null;
    const openId = location.state?.openActivityId || fromQuery;
    if (!openId) return;
    setActiveActivityTab('All activities');
    setExpandedActivityId(openId);
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search]);

  const openLogActivity = (type, followUpFrom = null) => {
    setEditingActivity(null);
    setEditingActivityEntity(null);
    setFollowUpFromActivity(followUpFrom);
    setDrawerActivityType(type);
    setLogDrawerOpen(true);
  };
  const openComposeEmail = () => setComposeEmailOpen(true);
  const openEditActivity = (activity) => {
    // A rolled-up row (see fetchActivities' rollup comment) really belongs
    // to a linked contact, not this page's own entity — edit it in that
    // context (e.g. Associated With pre-fills relative to the Individual
    // itself, not this page's Entity), not as if it were logged against
    // this Entity.
    const owner = (activity.commercial_entity && String(activity.commercial_entity.id) !== String(entity.id))
      ? activity.commercial_entity
      : entity;
    setEditingActivity(activity);
    setEditingActivityEntity(owner);
    setFollowUpFromActivity(null);
    setDrawerActivityType(activity.activity_type);
    setLogDrawerOpen(true);
  };
  const handleActivitySaved = (saved) => {
    setActivities((prev) => {
      const exists = prev.some((a) => a.id === saved.id);
      const next = exists ? prev.map((a) => (a.id === saved.id ? saved : a)) : [saved, ...prev];
      return [...next].sort((a, b) => (a.activity_date < b.activity_date ? 1 : a.activity_date > b.activity_date ? -1 : b.id - a.id));
    });
  };
  // A moved Activity leaves this Entity's feed outright — same as a delete,
  // rather than re-checking whether it still qualifies via rollup/
  // association, since moving it here always means "this doesn't belong on
  // this page anymore." Worst case (it was moved to a linked contact whose
  // Activities still roll up here) it simply reappears on the next full
  // reload, same as fetchActivities would return it fresh.
  const handleActivityMoved = (moved) => {
    setActivities((prev) => prev.filter((a) => a.id !== moved.id));
  };
  // Task Done/Open toggle (issue #219) — a dedicated PATCH, not a resend of
  // the whole row through the edit drawer's PUT, same "one small action, one
  // small endpoint" shape as the rest of this page's inline controls (e.g.
  // patchField below). Any user can flip it regardless of who the Task is
  // assigned to, so there's no ownership check here.
  const toggleActivityDone = async (activity, done) => {
    try {
      await axios.patch(`/api/activities/${activity.id}/${done ? 'complete' : 'reopen'}`);
      setActivities((prev) => prev.map((a) => (a.id === activity.id ? { ...a, is_done: done } : a)));
      dismissError();
    } catch (err) {
      showError(err, 'Could not update Task status');
    }
  };
  // Saves the address a Customer asked for in the portal as a Ship To
  // address (issue #339). The server then ticks every open request done
  // and emails the requester, so both lists are reloaded.
  const acceptAddressRequest = async (activity) => {
    if (!(await askConfirm(`Add ${addressRequestLine(activity.requested_address)} as a Ship To address? The customer will be emailed that they can choose it at checkout.`, { confirmLabel: 'Add' }))) return;
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/address-requests/${activity.id}/accept`);
      setEntity(res.data.data);
      setAddressesOpen(true);
      fetchActivities();
      dismissError();
    } catch (err) {
      showError(err, 'Could not accept the address');
    }
  };
  const deleteActivityRow = async (activityId) => {
    if (!(await askConfirm('Delete this Activity?', { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/activities/${activityId}`);
      setActivities((prev) => prev.filter((a) => a.id !== activityId));
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  // Option lists for the inline-edit selects (Source, Tier, Price
  // Group, Payment Term) — same reference data CommercialEntityForm loads,
  // fetched here too since this page renders its own select controls
  // instead of embedding that form for the read/edit-in-place fields.
  useEffect(() => {
    axios.get('/api/relationship-types', { params: { status: 'active' } }).then((res) => setRelationshipTypes(res.data.data || [])).catch(() => setRelationshipTypes([]));
    // No category filter — this page's Link Role dropdown (LinkRoleLabel)
    // covers both People and Companies rows, unlike AddLinkDrawer's own
    // fetch which only ever needs one category per drawer open.
    axios.get('/api/link-role-types', { params: { status: 'active' } }).then((res) => setLinkRoleTypes(res.data.data || [])).catch(() => setLinkRoleTypes([]));
    axios.get('/api/sources', { params: { status: 'active' } }).then((res) => setSources(res.data.data || [])).catch(() => setSources([]));
    axios.get('/api/legal-entity-types', { params: { status: 'active' } }).then((res) => setLegalEntityTypes(res.data.data || [])).catch(() => setLegalEntityTypes([]));
    axios.get('/api/legal-entity-types/registers').then((res) => setBusinessRegisters(res.data.data || [])).catch(() => setBusinessRegisters([]));
    axios.get('/api/customer-tiers', { params: { status: 'active' } }).then((res) => setCustomerTiers(res.data.data || [])).catch(() => setCustomerTiers([]));
    axios.get('/api/currencies', { params: { limit: 100, status: 'active' } }).then((res) => setCurrencies(res.data.data || [])).catch(() => setCurrencies([]));
    axios.get('/api/customer-price-groups', { params: { status: 'active' } }).then((res) => setCustomerPriceGroups(res.data.data || [])).catch(() => setCustomerPriceGroups([]));
    axios.get('/api/product-price-groups').then((res) => setProductPriceGroups((res.data.data || []).filter((g) => g.is_active))).catch(() => setProductPriceGroups([]));
    axios.get('/api/payment-terms', { params: { status: 'active' } }).then((res) => setPaymentTerms(res.data.data || [])).catch(() => setPaymentTerms([]));
    axios.get('/api/tax-handling-types', { params: { status: 'active' } }).then((res) => setTaxHandlingTypes(res.data.data || [])).catch(() => setTaxHandlingTypes([]));
    axios.get('/api/shipping-services', { params: { status: 'all' } }).then((res) => setShippingServices(res.data.data || [])).catch(() => setShippingServices([]));
    axios.get('/api/address-types', { params: { status: 'active' } }).then((res) => setAddressTypes(res.data.data || [])).catch(() => setAddressTypes([]));
    axios.get('/api/phone-types', { params: { status: 'active' } }).then((res) => setPhoneTypes(res.data.data || [])).catch(() => setPhoneTypes([]));
    axios.get('/api/email-types', { params: { status: 'active' } }).then((res) => setEmailTypes(res.data.data || [])).catch(() => setEmailTypes([]));
  }, []);

  // Options for the Default Options checklist below — scoped to this
  // record's own current shipping_service_id, refetched whenever that
  // changes (including via patchField's own clear-on-Service-change, which
  // also empties entity.default_options — see patchCommercialEntityField).
  useEffect(() => {
    const shippingServiceId = entity?.shipping_service_id;
    if (!shippingServiceId) { setOptionsForService([]); return; }
    axios.get('/api/shipping-options', { params: { shipping_service_id: shippingServiceId, status: 'active' } })
      .then((res) => setOptionsForService(res.data.data || []))
      .catch(() => setOptionsForService([]));
  }, [entity?.shipping_service_id]);

  // Backs every InlineEditField on this page — PATCHes just the one field,
  // then swaps in the server's full response (same shape as fetchEntity's)
  // so any cascade effect (e.g. Tier's default Payment Term) shows
  // immediately without a second round-trip.
  const patchField = async (field, rawValue) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/field`, { field, value: preparePatchValue(field, rawValue) });
    setEntity(res.data.data);
  };

  // First/Last Name save through this instead of patchField directly, so
  // Display Name keeps following them the same way it does while adding a
  // record (CommercialEntityForm's own nameManuallyEdited) — only while
  // Display Name still exactly matches "First Last", so a deliberately
  // customized Display Name is never silently overwritten.
  const patchNameField = async (field, value) => {
    const wasAutoDerived = entity.type === 'individual'
      && String(entity.name || '').trim() === `${entity.first_name || ''} ${entity.last_name || ''}`.trim();
    await patchField(field, value);
    if (wasAutoDerived) {
      const newFirst = field === 'first_name' ? value : entity.first_name;
      const newLast = field === 'last_name' ? value : entity.last_name;
      await patchField('name', `${newFirst || ''} ${newLast || ''}`.trim());
    }
  };

  // Default Options has no single "blur" moment the way an InlineEditField
  // does (it's a checklist, not one scalar value) — each toggle sends the
  // whole replacement list immediately instead, same auto-save idiom as
  // every other row, just against its own endpoint (see
  // commercialEntityController.patchCommercialEntityDefaultOptions).
  const toggleDefaultOption = async (optionId, checked) => {
    const current = (entity.default_options || []).map((o) => o.shipping_option.id);
    const next = checked ? [...current, optionId] : current.filter((oid) => oid !== optionId);
    const res = await axios.patch(`/api/commercial-entities/${id}/default-options`, { shipping_option_ids: next });
    setEntity(res.data.data);
  };

  // Markets — every add/remove/Segment/primary change saves the whole
  // replacement list straight away, same idiom as Default Options above.
  const saveMarkets = async (next) => {
    setSavingMarkets(true);
    try {
      const res = await axios.put(`/api/commercial-entities/${id}/markets`, {
        markets: next.map((r) => ({ market_id: r.market_id, market_segment_id: r.market_segment_id, is_primary: r.is_primary })),
      });
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSavingMarkets(false);
  };

  // Channels — same whole-list save as Markets.
  const saveChannels = async (next) => {
    setSavingChannels(true);
    try {
      const res = await axios.put(`/api/commercial-entities/${id}/channels`, {
        channels: next.map((r) => ({ channel_id: r.channel_id, is_primary: r.is_primary })),
      });
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSavingChannels(false);
  };

  // Operators — same whole-list save as Markets.
  const saveOperators = async (next) => {
    setSavingOperators(true);
    try {
      const res = await axios.put(`/api/commercial-entities/${id}/operators`, {
        operators: next.map((r) => ({ operator_id: r.operator_id, is_primary: r.is_primary })),
      });
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSavingOperators(false);
  };

  // Asks the register about the Business Number again (Legal card's Check
  // ABN), even when it has been checked before.
  const checkBusinessNumber = async () => {
    setLegalOpen(true);
    setCheckingNumber(true);
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/business-number-check`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, "Couldn't check the Business Number");
    }
    setCheckingNumber(false);
  };

  // Re-runs the URL-lookup fetch against this record's own saved Website
  // and overwrites About Us/Logo/Socials with whatever comes back — same
  // "deliberate click always overwrites" behaviour as the create form's
  // own Fetch button. Mainly useful for a Prospect/Customer whose logo was
  // captured back when this only picked up a tiny favicon.
  const refreshFromWebsite = async () => {
    setRefreshingWebsite(true);
    setWebFetchWarnings({});
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/refresh-website-info`);
      setEntity(res.data.data);
      setWebFetchWarnings(res.data.warnings || {});
      dismissError();
    } catch (err) {
      showError(err, "Couldn't refresh from that site");
    }
    setRefreshingWebsite(false);
  };

  // Manual fallback for a site whose auto-fetch can't find or reach a
  // usable logo — uploads straight into the Media Library (category
  // entity_logo) then points this record at it via the same patchField
  // path as every other field on this page.
  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/commercial-entities/logo', formData);
      await patchField('logo_media_image_id', res.data.data.logo.id);
      dismissError();
    } catch (err) {
      showError(err, 'Could not upload logo');
    }
    setUploadingLogo(false);
  };

  const removeLogo = async () => {
    try {
      await patchField('logo_media_image_id', null);
      dismissError();
    } catch (err) {
      showError(err, 'Could not remove logo');
    }
  };

  // Addresses/Emails/Phones — inline row-level add/edit-one-field/remove,
  // same click-to-edit-and-auto-save idiom as every scalar field above, now
  // extended to these collections instead of routing them through the full
  // Edit overlay. patchXField deliberately doesn't catch — InlineEditField's
  // own commit() awaits onSave and shows the rejection inline, same as
  // patchField above.
  const addAddress = async () => {
    setAddressesOpen(true);
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/addresses`, {});
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Add address failed');
    }
  };
  const patchAddressField = async (rowId, field, value) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/addresses/${rowId}/field`, { field, value });
    setEntity(res.data.data);
  };
  // An Australian Suburb, State and Postcode are saved together, so
  // Australia Post checks them once, as a whole (#158).
  const patchAddressLocality = async (rowId, fields) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/addresses/${rowId}/field`, { fields });
    setEntity(res.data.data);
  };
  // Re-check: Australia Post's check again, in one action (#158).
  const [recheckingAddressId, setRecheckingAddressId] = useState(null);
  const recheckAddress = async (rowId) => {
    setRecheckingAddressId(rowId);
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/addresses/${rowId}/check`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Could not check the Address');
    }
    setRecheckingAddressId(null);
  };
  // A Country change keeps the parts both countries share and blanks the
  // ones the new Country doesn't show (#157), in one save.
  const patchAddressCountry = async (address, country) => {
    const dropped = Object.fromEntries(Object.keys(partsDroppedFor(country)).filter((f) => address[f]).map((f) => [f, '']));
    await patchAddressLocality(address.id, { country, ...dropped });
  };
  const removeAddress = async (rowId) => {
    if (!(await askConfirm('Remove this address?', { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/addresses/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove address failed');
    }
  };
  // Undoes a Google auto-fill (Opening Hours lookup filling an empty
  // Address — see runOpeningHoursLookup) — a plain delete under the hood
  // since the row only ever exists because Google's search filled a gap,
  // but skips removeAddress's confirm() prompt: this is a one-click undo
  // of something that was itself automatic, not a deliberate delete of the
  // user's own data.
  const undoAddressAutoFill = async (rowId) => {
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/addresses/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Restore failed');
    }
  };
  // Same combined Google Places lookup as OpeningHoursCard.js's own pill —
  // the Addresses accordion's "Address Stale" pill is a second entry point
  // into the identical call, not a separate address-only lookup (there
  // isn't one — see googlePlacesService.js's single combined search).
  const handleAddressLookup = async () => {
    if (addressLookingUp) return;
    setAddressLookingUp(true);
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/opening-hours/lookup`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Look up failed');
    }
    setAddressLookingUp(false);
  };
  // Marks one address Primary (#165) — the server un-marks every sibling in
  // the same transaction, so this is a single call.
  const setPrimaryAddress = async (rowId) => {
    try {
      const res = await axios.patch(`/api/commercial-entities/${id}/addresses/${rowId}/primary`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Could not set Primary address');
    }
  };

  const commitEmailDraft = async () => {
    if (!emailDraft.email.trim()) return;
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/emails`, emailDraft);
      setEntity(res.data.data);
      setEmailDraft({ email_type_id: '', email: '' });
      setShowEmailDraft(false);
      dismissError();
    } catch (err) {
      showError(err, 'Add email failed');
    }
  };
  const patchEmailField = async (rowId, field, value) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/emails/${rowId}/field`, { field, value });
    setEntity(res.data.data);
  };
  const removeEmail = async (rowId) => {
    if (!(await askConfirm('Remove this email?', { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/emails/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove email failed');
    }
  };

  const commitPhoneDraft = async () => {
    if (!phoneDraft.phone_number.trim()) return;
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/phones`, phoneDraft);
      setEntity(res.data.data);
      setPhoneDraft({ phone_type_id: '', phone_number: '' });
      setShowPhoneDraft(false);
      dismissError();
    } catch (err) {
      showError(err, 'Add phone failed');
    }
  };
  const patchPhoneField = async (rowId, field, value) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/phones/${rowId}/field`, { field, value });
    setEntity(res.data.data);
  };
  const removePhone = async (rowId) => {
    if (!(await askConfirm('Remove this phone?', { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/phones/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove phone failed');
    }
  };

  const commitPriceGroupDraft = async (draft) => {
    if (!draft.product_price_group_id || !draft.customer_price_group_id) return;
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/price-groups`, draft);
      setEntity(res.data.data);
      setPriceGroupDraft({ product_price_group_id: '', customer_price_group_id: '' });
      setShowPriceGroupDraft(false);
      dismissError();
    } catch (err) {
      showError(err, 'Add Price Group failed');
    }
  };
  const setPriceGroupDraftField = (field, value) => {
    const next = { ...priceGroupDraft, [field]: value };
    setPriceGroupDraft(next);
    commitPriceGroupDraft(next);
  };
  const patchPriceGroupField = async (rowId, field, value) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/price-groups/${rowId}/field`, { field, value });
    setEntity(res.data.data);
  };
  const removePriceGroup = async (rowId) => {
    if (!(await askConfirm('Remove this Price Group?', { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/price-groups/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove Price Group failed');
    }
  };

  // People/Companies — same generic CommercialEntityLink, split by the
  // *other* entity's own type for the two cards below (not link_role.category,
  // which describes the role's intended side and stays fixed regardless of
  // which end of the link this page is viewing from — filtering on it would
  // put an incoming link from an Organisation under the People card whenever
  // the role was authored for linking individuals). Add goes through
  // AddLinkDrawer (search-existing-or-create-new); Remove deletes the link
  // only, never the linked record itself. Each card also folds in incoming
  // links (someone else's outgoing link pointing at this entity) so a link
  // is visible from both ends — those rows are read-only (no Remove) since
  // removing them belongs on the owning entity's own page.
  const outgoingLinks = (entity?.links || []).map((r) => ({ ...r, direction: 'out' }));
  const incomingLinks = (entity?.incoming_links || []).map((r) => ({ ...r, direction: 'in' }));
  const allLinks = [...outgoingLinks, ...incomingLinks];
  const otherOf = (r) => (r.direction === 'out' ? r.related_entity : r.commercial_entity);
  const people = allLinks.filter((r) => otherOf(r)?.type === 'individual');
  const companies = allLinks.filter((r) => otherOf(r)?.type === 'organization');
  // Default "Associated With" options on LogActivityDrawer — the
  // Individuals already linked to this Organisation via the People card
  // above, shown before typing; the field's live search covers everyone
  // else (any Individual or Organisation in the tenant).
  const contactOptions = people.map((r) => {
    const other = r.direction === 'out' ? r.related_entity : r.commercial_entity;
    return { id: other.id, name: other.name };
  });
  const removeLink = async (rowId, label) => {
    if (!(await askConfirm(`Remove ${label}?`, { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/links/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove failed');
    }
  };
  const patchLinkEndedAt = async (rowId, value) => {
    try {
      const res = await axios.patch(`/api/commercial-entities/${id}/links/${rowId}/field`, { field: 'ended_at', value });
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Update failed');
    }
  };
  const endLink = (rowId) => patchLinkEndedAt(rowId, todayInTenant());
  const undoEndLink = (rowId) => patchLinkEndedAt(rowId, null);
  const editLinkEndedAt = (rowId, date) => patchLinkEndedAt(rowId, date);
  const patchLinkRole = async (rowId, linkRoleTypeId) => {
    try {
      const res = await axios.patch(`/api/commercial-entities/${id}/links/${rowId}/field`, { field: 'link_role_type_id', value: linkRoleTypeId });
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Update failed');
    }
  };

  // Trade Show Attendance — a growing history, unlike the single Source
  // field: each row is one time this entity was met at (or exhibited at)
  // a Trade Show instance, added again next year rather than overwritten.
  const tradeShowAttendances = entity?.trade_show_attendances || [];
  const removeTradeShowAttendance = async (rowId, label) => {
    if (!(await askConfirm(`Remove ${label}?`, { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/trade-show-attendances/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove failed');
    }
  };

  // Relationship card — what this record IS to the business (Customer,
  // Supplier, Manufacturer...) and its own Stage in each, independent of any
  // Link Role/People-Companies link above. Prospect can only be removed
  // while at a Stage that doesn't require commercial terms, Customer never
  // (see removeCommercialEntityRelationship). A record holding one of
  // Prospect/Customer can't add the other; a Tier change never moves
  // either's Stage. Contact is the fallback a record holds when it holds
  // nothing else: never offered here, never removable, and dropped by the
  // backend as soon as any other Type is added.
  const heldRelationshipTypeIds = new Set((entity?.relationships || []).map((r) => r.relationship_type_id));
  const holdsLifecycleType = isCustomerEntity(entity);
  const addableRelationshipTypes = relationshipTypes.filter((rt) => !heldRelationshipTypeIds.has(rt.id)
    && rt.system_key !== 'contact'
    && !(holdsLifecycleType && LIFECYCLE_TYPE_KEYS.includes(rt.system_key)));
  const addRelationship = async (relationshipTypeId) => {
    try {
      const res = await axios.post(`/api/commercial-entities/${id}/relationships`, { relationship_type_id: relationshipTypeId });
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Add failed');
    }
  };
  // A Stage requiring commercial terms asks for the Tier (and Customer
  // Price Group) first when this record doesn't have them yet — the select
  // keeps its old value until the prompt confirms.
  const [pendingStageMove, setPendingStageMove] = useState(null);
  const sendRelationshipStage = async (rowId, stageId, terms = {}) => {
    const res = await axios.patch(`/api/commercial-entities/${id}/relationships/${rowId}/field`, { field: 'relationship_stage_id', value: stageId, ...terms });
    setEntity(res.data.data);
  };
  const patchRelationshipStage = async (rowId, stageId) => {
    const row = (entity.relationships || []).find((r) => r.id === rowId);
    const stage = (relationshipTypes.find((rt) => rt.id === row?.relationship_type_id)?.stages || []).find((s) => String(s.id) === String(stageId));
    // A Prospect moved to a closed Stage needs a reason — the Close dialog
    // takes it from here, with the Stage pre-selected (issue #246). Moving
    // a closed one back to an open Stage is a Reopen, which the server
    // logs the same as the Reopen action.
    if (row?.relationship_type?.system_key === 'prospect' && stage && !stage.is_open) {
      setClosingStageId(stage.id);
      return;
    }
    if (stage?.requires_commercial_terms && !hasCommercialTerms(entity)) {
      setPendingStageMove({ rowId, stage });
      return;
    }
    await sendRelationshipStage(rowId, stageId);
  };
  // Marks one relationship Principal (the Type/Stage a multi-Type entity is
  // primarily identified by on Market Intel's All Entities screen) — the
  // server un-marks every sibling in the same transaction, same shape as
  // setPrimaryAddress above.
  const setPrincipalRelationship = async (rowId) => {
    try {
      const res = await axios.patch(`/api/commercial-entities/${id}/relationships/${rowId}/principal`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Could not set Principal relationship');
    }
  };
  // Won — Convert to Customer, offered on a Prospect at an open Stage
  // (ConvertToCustomerDialog). Refetches Activities too, for the System
  // Activity the conversion logs.
  const [convertingToCustomer, setConvertingToCustomer] = useState(false);
  // Close (issue #246): null while shut, true from the Close action, or the
  // closed Stage id picked in the Stage field. Close and Reopen both log a
  // System Activity, so Activities refetch after either.
  const [closingStageId, setClosingStageId] = useState(null);
  const [reopening, setReopening] = useState(false);
  const removeRelationship = async (rowId, label) => {
    if (!(await askConfirm(`Remove ${label}?`, { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      const res = await axios.delete(`/api/commercial-entities/${id}/relationships/${rowId}`);
      setEntity(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Remove failed');
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (!entity) return <div style={{ padding: 24 }}>Record not found.</div>;

  const isOrganization = entity.type === 'organization';
  const businessNumberType = entity.business_number_type || businessNumberTypeForCountry(entity.primary_address?.country);
  // The register that checks this Business Number type, if any.
  const numberRegister = businessRegisters.find((r) => r.numberType === businessNumberType) || null;
  // Cards follow the Entity Cards grid (issue #249): a card shows when any
  // Relationship Type held ticks it. Record cards also show while they hold
  // records, so history stays in view when a relationship changes.
  const tickedCards = tickedCardKeys(entity);
  const showsCard = (key) => tickedCards.has(key);
  const addRelationshipMenuItems = addableRelationshipTypes.map((rt) => ({
    key: `add-relationship-${rt.id}`,
    label: `Add ${rt.name}`,
    onClick: () => addRelationship(rt.id),
  }));
  // Only a record whose Prospect or Customer relationship sits at a Stage
  // requiring commercial terms (Opportunity or Active by default) can be
  // quoted or ordered — same rule quoteController/salesOrderController
  // enforce.
  const canQuote = (entity.relationships || []).some((r) => LIFECYCLE_TYPE_KEYS.includes(r.relationship_type?.system_key) && r.stage?.requires_commercial_terms);
  const crumb = backCrumb(entity, location.state?.origin);

  return (
    <div className="commercial-entity-detail-page" style={{ margin: '32px 24px 48px' }}>
      {pendingStageMove && (
        <CommercialTermsPrompt
          entity={entity}
          stageName={pendingStageMove.stage.name}
          onCancel={() => setPendingStageMove(null)}
          onConfirm={async (terms) => {
            try {
              await sendRelationshipStage(pendingStageMove.rowId, pendingStageMove.stage.id, terms);
              dismissError();
              setPendingStageMove(null);
            } catch (err) {
              showError(err, 'Move failed');
              throw err;
            }
          }}
        />
      )}
      {convertingToCustomer && (
        <ConvertToCustomerDialog
          entity={entity}
          onCancel={() => setConvertingToCustomer(false)}
          onConverted={(updated) => {
            setEntity(updated);
            setConvertingToCustomer(false);
            fetchActivities();
          }}
        />
      )}
      {closingStageId && (
        <CloseProspectDialog
          entity={entity}
          initialStageId={closingStageId === true ? undefined : closingStageId}
          onCancel={() => setClosingStageId(null)}
          onClosed={(updated) => {
            setEntity(updated);
            setClosingStageId(null);
            fetchActivities();
          }}
        />
      )}
      {reopening && (
        <ReopenProspectDialog
          entity={entity}
          onCancel={() => setReopening(false)}
          onReopened={(updated) => {
            setEntity(updated);
            setReopening(false);
            fetchActivities();
          }}
        />
      )}
      <PageHeader
        crumbs={[{ label: crumb.label, onClick: () => navigate(crumb.path) }, { label: entity.name }]}
        title={entity.name}
        meta={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>{TYPE_LABELS[entity.type] || entity.type}</span>
            <Pill color={entity.is_active ? '#1b5e20' : '#b91c1c'} background={entity.is_active ? '#e3f3e5' : '#fef2f2'}>
              {entity.is_active ? 'Active' : 'Inactive'}
            </Pill>
            {/* Relationship card hidden by the grid — adding a Relationship
                Type stays reachable from here. */}
            {!showsCard('relationship') && addRelationshipMenuItems.length > 0 && (
              <RowActionsMenu items={addRelationshipMenuItems} ariaLabel="Add Relationship" />
            )}
          </div>
        }
      />

      <div style={{ background: '#eceef1', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 390px) 1fr minmax(320px, 390px)', gap: 16, alignItems: 'start' }}>
        {/* Left — static record data. Each section (Identity, then every
            Accordion below) is its own card — HubSpot's company-record
            layout, where Address Details/Categories/etc. each get their own
            card rather than sharing one long panel — with maxHeight/
            overflowY on the wrapper so a fully-expanded stack scrolls inside
            its own column rather than growing taller than the middle/right
            columns next to it. */}
        <div style={{ minWidth: 0, maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', paddingRight: 4 }}>
          {/* Identity always stays open — unlike the sections below, this
              is the record's primary identification, not detail worth
              collapsing away. Logo lives here, not as its own block, since
              it's part of identifying the record at a glance. */}
          <div style={{ ...panelStyle, padding: '16px', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#1a1a1a', marginBottom: 10 }}>
              Identity
            </div>
            {/* Same image slot for both types — an Organisation's logo, an
                Individual's photo. */}
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              {entity.logo ? (
                <img src={thumbFileUrl(entity.logo.id)} alt={isOrganization ? 'Logo' : 'Photo'} style={{ maxWidth: 160, maxHeight: 160, objectFit: 'contain' }} />
              ) : (
                <div style={{ width: 160, height: 100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', border: '1px dashed #c7cdd6', borderRadius: 6, fontSize: 12, color: '#999' }}>
                  {isOrganization ? 'No logo' : 'No photo'}
                </div>
              )}
              {canUpdate && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 6 }}>
                <label style={{ fontSize: 12, color: '#1976d2', fontWeight: 500, cursor: uploadingLogo ? 'not-allowed' : 'pointer' }}>
                  {uploadingLogo ? 'Uploading...' : entity.logo ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo} disabled={uploadingLogo} style={{ display: 'none' }} />
                </label>
                {entity.logo && (
                  <button type="button" onClick={removeLogo} style={{ background: 'none', border: 'none', color: '#ff6f61', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}>
                    Remove
                  </button>
                )}
              </div>
              )}
            </div>
            {entity.type === 'individual' && (
              <>
                <InlineEditField label="First Name" value={entity.first_name} onSave={(v) => patchNameField('first_name', v)} />
                <InlineEditField label="Last Name" value={entity.last_name} onSave={(v) => patchNameField('last_name', v)} />
                <InlineEditField label="Job Title" value={entity.job_title} onSave={(v) => patchField('job_title', v)} />
              </>
            )}
            <InlineEditField label="Display Name" value={entity.name} onSave={(v) => patchField('name', v)} />
            {isOrganization && (
              <>
                <InlineEditField
                  label="Website"
                  labelExtra={canUpdate && entity.website && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); refreshFromWebsite(); }}
                      disabled={refreshingWebsite}
                      style={{ background: 'none', border: 'none', color: '#1976d2', cursor: refreshingWebsite ? 'not-allowed' : 'pointer', textDecoration: 'underline', fontSize: 12, textTransform: 'none', letterSpacing: 0, padding: 0 }}
                    >
                      {refreshingWebsite ? 'Refreshing...' : 'Refresh from Website'}
                    </button>
                  )}
                  value={entity.website}
                  onSave={(v) => patchField('website', v)}
                  placeholder="e.g., acme.com.au"
                  formatDisplay={(v) => externalLinkDisplay(v)}
                />
                {webFetchWarnings.general && <div style={{ color: '#b45309', fontSize: 12, marginTop: -6, marginBottom: 8 }}>{webFetchWarnings.general}</div>}
                {webFetchWarnings.logo && <div style={{ color: '#b45309', fontSize: 12, marginTop: -6, marginBottom: 8 }}>Logo: {webFetchWarnings.logo}</div>}
              </>
            )}
            {isOrganization && (
              <InlineEditField
                label="Locale"
                value={entity.locale}
                onSave={(v) => patchField('locale', v)}
                placeholder="e.g., Canberra (for Google search API)"
              />
            )}
            <InlineEditField
              label="Added By"
              readOnly
              value={entity.created_at}
              formatDisplay={() => `${addedByLabel(entity)}, ${formatDate(entity.created_at)}`}
            />
          </div>
          {showsCard('relationship') && (
          <Accordion
            title="Relationship"
            defaultOpen={false}
            menuItems={addRelationshipMenuItems.length ? addRelationshipMenuItems : undefined}
          >
            {(entity.relationships || []).map((r) => (
              <div key={r.id} style={childRowStyle}>
                {(!r.relationship_type?.system_key || (r.relationship_type.system_key === 'prospect' && !r.stage?.requires_commercial_terms)) && (
                  <>{canUpdate && (<button type="button" onClick={() => removeRelationship(r.id, r.relationship_type?.name)} title="Remove" style={removeRowBtnStyle}>×</button>)}</>
                )}
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {r.is_principal ? (
                    <Pill>Principal</Pill>
                  ) : (
                    <>{canUpdate && (<button type="button" onClick={() => setPrincipalRelationship(r.id)} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}>
                      Set as Principal
                    </button>)}</>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={draftFieldLabelStyle}>Type</div>
                  <div style={{ fontSize: 15 }}>{r.relationship_type?.name}</div>
                </div>
                <InlineEditField
                  label="Stage"
                  value={String(r.relationship_stage_id)}
                  onSave={(v) => patchRelationshipStage(r.id, v)}
                  type="select"
                  options={(relationshipTypes.find((rt) => rt.id === r.relationship_type_id)?.stages || []).map((s) => ({ value: String(s.id), label: s.name }))}
                  formatDisplay={() => r.stage?.name || '—'}
                />
                {r.relationship_type?.system_key === 'customer' && (
                  <InlineEditField
                    label="Customer Since"
                    labelExtra={<FieldHelp fieldKey="commercial-entities.customer-since" pageKey="commercial-entities" label="Customer Since">When this Customer started trading with you. The customer portal shows it as &quot;Ordering with us since&quot; the month and year; left blank, it uses the month of their first Sales Order.</FieldHelp>}
                    value={entity.customer_since || ''}
                    onSave={(v) => patchField('customer_since', v)}
                    type="date"
                  />
                )}
                {r.relationship_type?.system_key === 'customer' && (
                  <InlineEditField
                    label="Account Owner"
                    labelExtra={<FieldHelp fieldKey="commercial-entities.account-owner" pageKey="commercial-entities" label="Account Owner">The staff member who looks after this Customer. Portal alerts, such as new orders and quote decisions, go to them; with no owner, or an owner who has been switched off, they go to the Fallback Alert Emails on Sales Settings.</FieldHelp>}
                    value={entity.account_owner_user_id != null ? String(entity.account_owner_user_id) : ''}
                    onSave={(v) => patchField('account_owner_user_id', v)}
                    type="select"
                    options={[{ value: '', label: '(none)' }, ...accountOwners.map((u) => ({ value: String(u.id), label: u.name }))]}
                    formatDisplay={() => (entity.account_owner ? `${entity.account_owner.name}${entity.account_owner.is_active ? '' : ' (switched off)'}` : '—')}
                  />
                )}
                {r.relationship_type?.system_key === 'prospect' && r.stage && !r.stage.is_open && (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <div style={draftFieldLabelStyle}>Close Reason</div>
                      <div style={{ fontSize: 15 }}>{r.close_reason?.name || '—'}</div>
                    </div>
                    {r.revisit_activity && !r.revisit_activity.is_done && r.revisit_activity.activity_date && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={draftFieldLabelStyle}>Revisit</div>
                        <div style={{ fontSize: 15 }}>{formatDate(r.revisit_activity.activity_date)}</div>
                      </div>
                    )}
                  </>
                )}
                {canUpdate && r.relationship_type?.system_key === 'prospect' && r.stage?.is_open && (
                  <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
                    <button type="button" onClick={() => setConvertingToCustomer(true)} title="Convert this Prospect to a Customer at Active" style={relationshipActionStyle}>
                      Won — Convert to Customer
                    </button>
                    <button type="button" onClick={() => setClosingStageId(true)} title="Close this Prospect as Disqualified or Lost" style={relationshipActionStyle}>
                      Close
                    </button>
                  </div>
                )}
                {canUpdate && r.relationship_type?.system_key === 'prospect' && r.stage && !r.stage.is_open && (
                  <button type="button" onClick={() => setReopening(true)} title="Reopen this Prospect at an open Stage" style={{ ...relationshipActionStyle, marginTop: 4 }}>
                    Reopen
                  </button>
                )}
              </div>
            ))}
          </Accordion>
          )}
          {showsCard('classification') && (
          <Accordion
            title="Classification"
            open={classificationOpen}
            onToggle={() => setClassificationOpen((o) => !o)}
            menuItems={isOrganization ? [
              { key: 'add-market', label: 'Add Market', onClick: () => { setClassificationOpen(true); setClassificationAdding('market'); } },
              { key: 'add-channel', label: 'Add Outlet', onClick: () => { setClassificationOpen(true); setClassificationAdding('channel'); } },
              classificationEditing
                ? { key: 'done-classification', label: 'Done Editing', onClick: () => setClassificationEditing(false) }
                : { key: 'edit-classification', label: 'Edit Classification', onClick: () => { setClassificationOpen(true); setClassificationEditing(true); } },
            ] : undefined}
          >
            {isOrganization && (
              <>
                <ClassificationField
                  label="Markets"
                  rows={entity.markets}
                  labelOf={(r) => `${marketRowText(r)}${r.market?.is_active === false || r.market_segment?.is_active === false ? ' (deactivated)' : ''}`}
                  editing={classificationEditing}
                  adding={classificationAdding === 'market'}
                  addOptions={marketAddOptions(entity.markets, marketOptions, segmentOptions)}
                  onAdd={(value) => { setClassificationAdding(null); return marketRowFor(value, marketOptions, segmentOptions); }}
                  onCancelAdd={() => setClassificationAdding(null)}
                  onChange={saveMarkets}
                  disabled={savingMarkets}
                  labelStyle={draftFieldLabelStyle}
                />
                <ClassificationField
                  label="Outlets"
                  rows={entity.channels}
                  labelOf={(r) => `${r.channel?.name || ''}${r.channel?.is_active === false ? ' (deactivated)' : ''}`}
                  editing={classificationEditing}
                  adding={classificationAdding === 'channel'}
                  addOptions={heldAddOptions(channelOptions, entity.channels, 'channel_id')}
                  onAdd={(value) => { setClassificationAdding(null); return { channel_id: Number(value), channel: channelOptions.find((c) => String(c.id) === value) }; }}
                  onCancelAdd={() => setClassificationAdding(null)}
                  onChange={saveChannels}
                  disabled={savingChannels}
                  labelStyle={draftFieldLabelStyle}
                />
                {classificationEditing && (
                  <div style={{ marginBottom: 14 }}>
                    <button type="button" onClick={() => setClassificationEditing(false)} style={{ padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
                      Done
                    </button>
                  </div>
                )}
              </>
            )}
            <InlineEditField
              label="Source"
              value={entity.source_id != null ? String(entity.source_id) : ''}
              onSave={(v) => patchField('source_id', v)}
              type="select"
              options={[{ value: '', label: '(none)' }, ...sources.map((s) => ({ value: String(s.id), label: s.name }))]}
              formatDisplay={() => entity.source?.name || '—'}
            />
          </Accordion>
          )}
          {/* Legal (split back out of Classification once it gained the
              register check): Legal Entity, Business Number and the check. */}
          {showsCard('legal') && (
          <Accordion
            title="Legal"
            open={legalOpen}
            onToggle={() => setLegalOpen((o) => !o)}
            menuItems={canUpdate && numberRegister && entity.business_number ? [
              {
                key: 'check-business-number',
                label: checkingNumber ? `Checking ${businessNumberLabel(businessNumberType)}…` : `Check ${businessNumberLabel(businessNumberType)}`,
                disabled: checkingNumber,
                onClick: checkBusinessNumber,
              },
            ] : undefined}
          >
            <InlineEditField
              label="Legal Entity"
              value={entity.legal_entity_type_id != null ? String(entity.legal_entity_type_id) : ''}
              onSave={(v) => patchField('legal_entity_type_id', v)}
              type="select"
              options={[{ value: '', label: '(none)' }, ...legalEntityTypes.map((lt) => ({ value: String(lt.id), label: lt.name }))]}
              formatDisplay={() => entity.legal_entity_type?.name || '—'}
            />
            {/* Business Number (issue #286): one per business, its type
                starting from the primary address's country. */}
            <InlineEditField
              label="Business Number"
              labelExtra={businessNumberType === 'abn' && (
                <ExternalLink href={`https://abr.business.gov.au/Search/ResultsActive?SearchText=${encodeURIComponent(entity.name)}`} label="Look up ABN" />
              )}
              value={entity.business_number}
              onSave={(v) => patchField('business_number', v)}
              formatInput={businessNumberType === 'abn' ? spaceAbnInput : undefined}
              placeholder={`e.g., ${businessNumberLabel(businessNumberType)}`}
              formatDisplay={(v) => (v ? `${businessNumberLabel(businessNumberType)} ${formatBusinessNumber(businessNumberType, v)}` : '—')}
            />
            {entity.business_number && (
              <InlineEditField
                label="Number Type"
                value={businessNumberType}
                onSave={(v) => patchField('business_number_type', v)}
                type="select"
                options={BUSINESS_NUMBER_TYPES.map((t) => ({ value: t.key, label: t.label }))}
                formatDisplay={() => businessNumberLabel(businessNumberType)}
              />
            )}
            {numberRegister && entity.business_number && (
              <BusinessNumberCheckDetails entity={entity} register={numberRegister} legalEntityTypes={legalEntityTypes} />
            )}
          </Accordion>
          )}
          {/* Operator is what an Organisation does with the products it buys
              from you. */}
          {isOrganization && showsCard('operators') && (
          <Accordion
            title="Operators"
            open={operatorsOpen}
            onToggle={() => setOperatorsOpen((o) => !o)}
            menuItems={[
              { key: 'add-operator', label: 'Add Operator', onClick: () => { setOperatorsOpen(true); setOperatorsAdding(true); } },
              operatorsEditing
                ? { key: 'done-operators', label: 'Done Editing', onClick: () => setOperatorsEditing(false) }
                : { key: 'edit-operators', label: 'Edit Operators', onClick: () => { setOperatorsOpen(true); setOperatorsEditing(true); } },
            ]}
          >
            <ClassificationField
              label="Operators"
              rows={entity.operators}
              labelOf={(r) => `${r.operator?.name || ''}${r.operator?.is_active === false ? ' (deactivated)' : ''}`}
              editing={operatorsEditing}
              adding={operatorsAdding}
              addOptions={heldAddOptions(operatorOptions, entity.operators, 'operator_id')}
              onAdd={(value) => { setOperatorsAdding(false); return { operator_id: Number(value), operator: operatorOptions.find((o) => String(o.id) === value) }; }}
              onCancelAdd={() => setOperatorsAdding(false)}
              onChange={saveOperators}
              disabled={savingOperators}
              labelStyle={draftFieldLabelStyle}
            />
            {operatorsEditing && (
              <div style={{ marginBottom: 14 }}>
                <button type="button" onClick={() => setOperatorsEditing(false)} style={{ padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
                  Done
                </button>
              </div>
            )}
          </Accordion>
          )}
          {showsCard('social_media') && (
          <Accordion title="Social Media" defaultOpen={false}>
            {isOrganization && (
              <>
                <InlineEditField label="About Us" value={entity.about_us} onSave={(v) => patchField('about_us', v)} type="textarea" />
              </>
            )}
            <InlineEditField
              label="LinkedIn"
              value={entity.linkedin_url}
              onSave={(v) => patchField('linkedin_url', v)}
              placeholder="Profile URL"
              formatDisplay={(v) => externalLinkDisplay(v)}
            />
            <InlineEditField
              label="Facebook"
              value={entity.facebook_url}
              onSave={(v) => patchField('facebook_url', v)}
              placeholder="Profile URL"
              formatDisplay={(v) => externalLinkDisplay(v)}
            />
            <InlineEditField
              label="Instagram"
              value={entity.instagram_url}
              onSave={(v) => patchField('instagram_url', v)}
              placeholder="Profile URL"
              formatDisplay={(v) => externalLinkDisplay(v)}
            />
          </Accordion>
          )}
          {showsCard('currency') && (
            <Accordion
              title="Currency"
              defaultOpen={false}
              menuItems={[
                { key: 'cost-price-schedule', label: 'Cost Price Schedule', onClick: () => navigate(`/suppliers/${id}/cost-prices`) },
              ]}
            >
              {supplierInfo && !supplierInfo.currency_locked ? (
                <InlineEditField
                  label="Currency"
                  value={supplierInfo.currency_code || ''}
                  onSave={async (v) => {
                    const res = await axios.put(`/api/suppliers/${id}`, { currency_code: v });
                    setSupplierInfo((prev) => ({ ...prev, ...res.data.data }));
                  }}
                  type="select"
                  options={currencies.map((c) => ({ value: c.currency_code, label: `${c.currency_code} - ${c.currency_name}` }))}
                  formatDisplay={() => (supplierInfo.currency ? `${supplierInfo.currency.currency_code} - ${supplierInfo.currency.currency_name}` : '—')}
                />
              ) : (
                <div style={{ marginBottom: 10 }}>
                  <div style={draftFieldLabelStyle}>Currency</div>
                  <div style={{ fontSize: 15 }} title={supplierInfo?.currency_locked ? "Locked — this Supplier's Products already have Cost Price entries in this currency" : undefined}>
                    {supplierInfo?.currency ? `${supplierInfo.currency.currency_code} - ${supplierInfo.currency.currency_name}` : '—'}
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <div style={draftFieldLabelStyle}>Today's Rate</div>
                <div style={{ fontSize: 15 }} title={supplierInfo?.exchange_rate == null ? (supplierInfo?.currency_code ? 'No reference rate could be retrieved for this currency' : 'No currency set') : `Today's reference rate, ${getTenantHomeCurrency()} per ${supplierInfo.currency_code}`}>
                  {supplierInfo?.exchange_rate == null ? '—' : formatNumber(supplierInfo.exchange_rate, { decimals: 6 })}
                </div>
              </div>
            </Accordion>
          )}
          {/* Shown for a Prospect with no Tier yet too — a Tier (and a Customer
              Price Group) is needed before it can move to a Stage that
              requires commercial terms, so this is where it's set ahead of
              time. The Tier can't be cleared while at such a Stage, only
              changed. */}
          {showsCard('customer_terms') && (
          <Accordion title="Customer Terms" defaultOpen={false}>
            <InlineEditField
              label="Tier"
              labelExtra={<FieldHelp fieldKey="commercial-entities.tier" pageKey="commercial-entities" label="Tier">Classifies this Customer's commercial relationship — defaults Credit Terms, Payment Term, and Tax Display (inc/ex GST) preference. Independent of Price Group, which controls what they pay.</FieldHelp>}
              value={entity.tier_id != null ? String(entity.tier_id) : ''}
              onSave={(v) => patchField('tier_id', v)}
              type="select"
              options={[{ value: '', label: '(none)' }, ...customerTiers.map((t) => ({ value: String(t.id), label: t.name }))]}
              formatDisplay={() => entity.tier?.name || '—'}
            />
            <InlineEditField
              label="Payment Term"
              value={entity.overrides?.payment_term_id != null ? String(entity.overrides.payment_term_id) : ''}
              onSave={(v) => patchField('payment_term_id', v)}
              type="select"
              options={[{ value: '', label: '(Tier default)' }, ...paymentTerms.map((pt) => ({ value: String(pt.id), label: pt.name }))]}
              formatDisplay={() => (entity.payment_term?.name ? `${entity.payment_term.name}${entity.overrides?.payment_term_id == null ? ' (Tier default)' : ''}` : '—')}
            />
            <InlineEditField
              label={`Credit Limit (${getTenantHomeCurrency()})`}
              value={entity.overrides?.credit_limit ?? ''}
              onSave={(v) => patchField('credit_limit', v)}
              type="number"
              formatDisplay={() => (entity.credit_limit != null ? `${formatMoney(entity.credit_limit)}${entity.overrides?.credit_limit == null ? ' (Tier default)' : ''}` : '—')}
            />
            <InlineEditField
              label="Tax Handling Type"
              value={entity.overrides?.tax_handling_type_id != null ? String(entity.overrides.tax_handling_type_id) : ''}
              onSave={(v) => patchField('tax_handling_type_id', v)}
              type="select"
              options={[{ value: '', label: '(Tier default)' }, ...taxHandlingTypes.map((t) => ({ value: String(t.id), label: t.name }))]}
              formatDisplay={() => (entity.tax_handling_type ? `${entity.tax_handling_type.name}${entity.overrides?.tax_handling_type_id == null ? ' (Tier default)' : ''}` : '—')}
            />
          </Accordion>
          )}
          {/* One Customer Price Group per Product Price Group (issue #240).
              A Product uses the assignment on its own Product Price Group,
              else its nearest ancestor's, else the Tier's default Customer
              Price Group; with none, the Customer is priced at Retail. The draft's Product Price Group list leaves out any
              already assigned, so each can only be assigned once. */}
          {(showsCard('price_groups') || entity.price_groups?.length > 0) && (
          <Accordion
            title="Price Groups"
            open={priceGroupsOpen}
            onToggle={() => setPriceGroupsOpen((o) => !o)}
            menuItems={[{ key: 'add-price-group', label: 'Add Price Group', onClick: () => { setPriceGroupsOpen(true); setShowPriceGroupDraft(true); } }]}
          >
            {entity.price_groups?.length ? [...entity.price_groups]
              .sort((a, b) => String(a.product_price_group?.name || '').localeCompare(String(b.product_price_group?.name || '')))
              .map((pg) => (
                <div key={pg.id} style={childRowStyle}>
                  <>{canUpdate && (<button type="button" onClick={() => removePriceGroup(pg.id)} title="Remove Price Group" style={removeRowBtnStyle}>×</button>)}</>
                  <InlineEditField
                    label={pg.product_price_group?.name || '—'}
                    value={String(pg.customer_price_group_id)}
                    onSave={(v) => patchPriceGroupField(pg.id, 'customer_price_group_id', v)}
                    type="select"
                    options={customerPriceGroups.map((g) => ({ value: String(g.id), label: `${'  '.repeat(g.depth || 0)}${g.name}` }))}
                    formatDisplay={() => pg.customer_price_group?.name || '—'}
                  />
                </div>
              )) : (
                <div style={emptyNoteStyle}>
                  {entity.tier?.default_customer_price_group
                    ? `None assigned — priced at ${entity.tier.default_customer_price_group.name}, the ${entity.tier.name} Tier's default`
                    : 'None assigned — priced at Retail'}
                </div>
              )}
            {showPriceGroupDraft && (
              <div style={{ marginBottom: 6 }}>
                <label style={draftFieldLabelStyle}>Product Price Group</label>
                <select
                  className="styled-input"
                  autoFocus
                  value={priceGroupDraft.product_price_group_id}
                  onChange={(e) => setPriceGroupDraftField('product_price_group_id', e.target.value)}
                  style={{ width: '100%', fontSize: 14, marginBottom: 8 }}
                >
                  <option value="">Select...</option>
                  {productPriceGroups
                    .filter((g) => !(entity.price_groups || []).some((pg) => String(pg.product_price_group_id) === String(g.id)))
                    .map((g) => <option key={g.id} value={g.id}>{'  '.repeat(g.depth || 0)}{g.name}</option>)}
                </select>
                <label style={draftFieldLabelStyle}>Customer Price Group</label>
                <select
                  className="styled-input"
                  value={priceGroupDraft.customer_price_group_id}
                  onChange={(e) => setPriceGroupDraftField('customer_price_group_id', e.target.value)}
                  style={{ width: '100%', fontSize: 14 }}
                >
                  <option value="">Select...</option>
                  {customerPriceGroups.map((g) => <option key={g.id} value={g.id}>{'  '.repeat(g.depth || 0)}{g.name}</option>)}
                </select>
              </div>
            )}
          </Accordion>
          )}
          {showsCard('preferences') && (
          <Accordion title="Preferences" defaultOpen={false}>
            {/* Carrier first (issue #281) — saving a new Carrier clears the
                Service and default Options server-side; the Service list is
                only that Carrier's Services. */}
            <InlineEditField
              label="Carrier"
              value={entity.carrier_id != null ? String(entity.carrier_id) : ''}
              onSave={(v) => patchField('carrier_id', v)}
              type="select"
              options={[{ value: '', label: '(none)' }, ...carrierChoices(shippingServices, entity.carrier_id).map((c) => ({ value: String(c.id), label: c.name }))]}
              formatDisplay={() => entity.carrier?.name || '—'}
            />
            <InlineEditField
              label="Shipping Service"
              value={entity.shipping_service_id != null ? String(entity.shipping_service_id) : ''}
              onSave={(v) => patchField('shipping_service_id', v)}
              type="select"
              options={[{ value: '', label: '(none)' }, ...servicesForCarrier(shippingServices, entity.carrier_id, entity.shipping_service_id).map((sm) => ({ value: String(sm.id), label: sm.description }))]}
              formatDisplay={() => entity.shipping_service?.description || '—'}
            />
            {optionsForService.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3 }}>Shipping Options</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                  {optionsForService.map((option) => (
                    <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 400, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(entity.default_options || []).some((o) => o.shipping_option.id === option.id)}
                        onChange={(e) => toggleDefaultOption(option.id, e.target.checked)}
                        disabled={!canUpdate}
                      />
                      {option.description}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {/* Issue #445: Pack offers the Packing List once the order is
                packed; it can still be printed any time when off. */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3 }}>Offer Packing List</div>
              <ToggleSwitch
                checked={entity.offer_packing_list !== false}
                onChange={(v) => patchField('offer_packing_list', v)}
                disabled={!canUpdate}
                title="Offer to print the Packing List when this Customer's order is packed"
              />
            </div>
          </Accordion>
          )}
          <Accordion
            title="Addresses"
            open={addressesOpen}
            onToggle={() => setAddressesOpen((o) => !o)}
            menuItems={[{ key: 'add-address', label: 'Add Address', onClick: addAddress }]}
            headerExtra={isOrganization && canUpdate && (
              <GoogleLookupPill
                stale={entity.address_stale}
                noMatch={entity.opening_hours_google_no_match}
                untried={!entity.primary_address?.updatedAt}
                staleLabel="Address Stale"
                checking={addressLookingUp}
                onClick={handleAddressLookup}
              />
            )}
          >
            {isOrganization && (
              <InlineEditField
                label="Google Maps Link"
                value={entity.google_maps_url}
                onSave={(v) => patchField('google_maps_url', v)}
                placeholder="Filled in automatically, or paste a Google Maps link"
                formatDisplay={(v) => externalLinkDisplay(v, 'Open in Google Maps')}
              />
            )}
            {entity.addresses?.length ? entity.addresses.map((a) => {
              const isAu = a.country === 'Australia';
              const warnings = addressWarnings(a);
              const localityParts = addressParts(a.country).filter((p) => p.key !== 'line1' && p.key !== 'line2');
              return (
              <div key={a.id} style={childRowStyle}>
                <>{canUpdate && (<button type="button" onClick={() => removeAddress(a.id)} title="Remove address" style={removeRowBtnStyle}>×</button>)}</>
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {a.is_primary ? (
                    <>
                      <Pill>Primary</Pill>
                      {canUpdate && a.google_auto_filled && (
                        <span
                          onClick={() => undoAddressAutoFill(a.id)}
                          title="Filled in by Google Places — click to remove"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            backgroundColor: '#2563eb',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1,
                            cursor: 'pointer',
                          }}
                        >
                          ↺
                        </span>
                      )}
                    </>
                  ) : (
                    <>{canUpdate && (<button type="button" onClick={() => setPrimaryAddress(a.id)} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}>
                      Set as Primary
                    </button>)}</>
                  )}
                  <AddressCheckPill address={a} />
                </div>
                <InlineEditField
                  label="Type"
                  value={a.address_type_id != null ? String(a.address_type_id) : ''}
                  onSave={(v) => patchAddressField(a.id, 'address_type_id', v)}
                  type="select"
                  options={[{ value: '', label: '(none)' }, ...addressTypes.map((t) => ({ value: String(t.id), label: t.name }))]}
                  formatDisplay={() => a.address_type?.name || '—'}
                />
                <InlineEditField
                  label="Country"
                  value={a.country || ''}
                  onSave={(v) => patchAddressCountry(a, v)}
                  type="select"
                  options={COUNTRIES.map((c) => ({ value: c, label: c }))}
                />
                <InlineEditField label="Line 1" value={a.line1} onSave={(v) => patchAddressField(a.id, 'line1', v)} />
                <AddressFieldWarning message={warnings.line1} />
                <InlineEditField label="Line 2" value={a.line2} onSave={(v) => patchAddressField(a.id, 'line2', v)} />
                {isAu ? (
                  <>
                    <AuLocalityInlineField
                      address={a}
                      onSave={(fields) => patchAddressLocality(a.id, fields)}
                      checkPill={<PostcodeCheckPill status={a.postcode_check} checking={recheckingAddressId === a.id} onRecheck={canUpdate ? () => recheckAddress(a.id) : undefined} />}
                    />
                    <AddressFieldWarning message={warnings.suburb || warnings.state || warnings.postcode} />
                  </>
                ) : localityParts.map((part) => (
                  <React.Fragment key={part.key}>
                    {part.options ? (
                      <InlineEditField
                        label={part.label}
                        value={findRegion(a.country, a.state)?.value || a.state || ''}
                        onSave={(v) => patchAddressField(a.id, 'state', v)}
                        type="select"
                        options={[
                          { value: '', label: '(none)' },
                          ...(a.state && !findRegion(a.country, a.state) ? [{ value: a.state, label: `${a.state} (not on list)` }] : []),
                          ...part.options,
                        ]}
                        formatDisplay={(v) => findRegion(a.country, v)?.label || v || '—'}
                      />
                    ) : (
                      <InlineEditField label={part.label} value={a[part.key]} onSave={(v) => patchAddressField(a.id, part.key, v)} />
                    )}
                    <AddressFieldWarning message={warnings[part.key]} />
                  </React.Fragment>
                ))}
              </div>
              );
            }) : <div style={emptyNoteStyle}>None recorded</div>}
          </Accordion>
          <Accordion
            title="Phones"
            open={phonesOpen}
            onToggle={() => setPhonesOpen((o) => !o)}
            menuItems={[{ key: 'add-phone', label: 'Add Phone', onClick: () => { setPhonesOpen(true); setShowPhoneDraft(true); } }]}
          >
            {entity.phones?.length ? entity.phones.map((p) => (
              <div key={p.id} style={childRowStyle}>
                <>{canUpdate && (<button type="button" onClick={() => removePhone(p.id)} title="Remove phone" style={removeRowBtnStyle}>×</button>)}</>
                <InlineEditField
                  label="Type"
                  value={p.phone_type_id != null ? String(p.phone_type_id) : ''}
                  onSave={(v) => patchPhoneField(p.id, 'phone_type_id', v)}
                  type="select"
                  options={[{ value: '', label: '(none)' }, ...phoneTypes.map((t) => ({ value: String(t.id), label: t.name }))]}
                  formatDisplay={() => p.phone_type?.name || '—'}
                />
                <InlineEditField label="Number" value={p.phone_number} onSave={(v) => patchPhoneField(p.id, 'phone_number', v)} />
              </div>
            )) : <div style={emptyNoteStyle}>None recorded</div>}
            {showPhoneDraft && (
              <div style={{ marginBottom: 6 }}>
                <label style={draftFieldLabelStyle}>Type</label>
                <select
                  className="styled-input"
                  value={phoneDraft.phone_type_id}
                  onChange={(e) => setPhoneDraft((prev) => ({ ...prev, phone_type_id: e.target.value }))}
                  style={{ width: '100%', fontSize: 14, marginBottom: 8 }}
                >
                  <option value="">(none)</option>
                  {phoneTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <label style={draftFieldLabelStyle}>New Phone</label>
                <input
                  className="styled-input"
                  autoFocus
                  value={phoneDraft.phone_number}
                  onChange={(e) => setPhoneDraft((prev) => ({ ...prev, phone_number: e.target.value }))}
                  onBlur={commitPhoneDraft}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPhoneDraft(); } }}
                  placeholder="e.g., 0400 000 000"
                  style={{ width: '100%', fontSize: 14 }}
                />
              </div>
            )}
          </Accordion>
          <Accordion
            title="Emails"
            open={emailsOpen}
            onToggle={() => setEmailsOpen((o) => !o)}
            menuItems={[
              { key: 'add-email', label: 'Add Email', onClick: () => { setEmailsOpen(true); setShowEmailDraft(true); } },
              ...(entity.sync && entity.emails?.length ? [{ key: 'pull-emails', label: 'Pull Emails', onClick: () => setPullEmailsOpen(true) }] : []),
            ]}
          >
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3 }}>Email Sync</div>
              <ToggleSwitch
                checked={!!entity.sync}
                onChange={(v) => patchField('sync', v)}
                disabled={!canUpdate || (!entity.sync && !entity.emails?.length)}
                title={!entity.sync && !entity.emails?.length ? 'Add an email below before turning on Email Sync' : "Match and log emails for this Entity's addresses"}
              />
            </div>
            {entity.emails?.length ? entity.emails.map((e) => (
              <div key={e.id} style={childRowStyle}>
                <>{canUpdate && (<button type="button" onClick={() => removeEmail(e.id)} title="Remove email" style={removeRowBtnStyle}>×</button>)}</>
                <InlineEditField
                  label="Type"
                  value={e.email_type_id != null ? String(e.email_type_id) : ''}
                  onSave={(v) => patchEmailField(e.id, 'email_type_id', v)}
                  type="select"
                  options={[{ value: '', label: '(none)' }, ...emailTypes.map((t) => ({ value: String(t.id), label: t.name }))]}
                  formatDisplay={() => e.email_type?.name || '—'}
                />
                <InlineEditField label="Email" value={e.email} onSave={(v) => patchEmailField(e.id, 'email', v)} />
              </div>
            )) : <div style={emptyNoteStyle}>None recorded</div>}
            {showEmailDraft && (
              <div style={{ marginBottom: 6 }}>
                <label style={draftFieldLabelStyle}>Type</label>
                <select
                  className="styled-input"
                  value={emailDraft.email_type_id}
                  onChange={(e) => setEmailDraft((prev) => ({ ...prev, email_type_id: e.target.value }))}
                  style={{ width: '100%', fontSize: 14, marginBottom: 8 }}
                >
                  <option value="">(none)</option>
                  {emailTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <label style={draftFieldLabelStyle}>New Email</label>
                <input
                  className="styled-input"
                  autoFocus
                  value={emailDraft.email}
                  onChange={(e) => setEmailDraft((prev) => ({ ...prev, email: e.target.value }))}
                  onBlur={commitEmailDraft}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitEmailDraft(); } }}
                  placeholder="name@example.com"
                  style={{ width: '100%', fontSize: 14 }}
                />
              </div>
            )}
          </Accordion>
          {isOrganization && <OpeningHoursCard entityId={id} entity={entity} onUpdate={setEntity} />}
        </div>

        {/* Middle — Activities (story #216): quick-action icons log a new
            Call/Meeting/Email/Message via LogActivityDrawer, tabs filter
            the list client-side (the fetched log is already a single small
            round-trip, see fetchActivities above), most recent first.
            Always expanded — no accordion toggle, unlike the placeholder
            this replaced, now that there's real content to show. */}
        <div style={{ ...panelStyle, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 4px' }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#1a1a1a', flexShrink: 0 }}>Activities</div>
            {/* Placeholder only — disabled until there's a real search
                endpoint to wire this up to (out of scope for story #216). */}
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9aa5b5' }} />
              <input
                className="styled-input"
                type="text"
                disabled
                title="Coming soon"
                placeholder="Search activities"
                style={{ width: '100%', fontSize: 13, height: 28, paddingLeft: 28 }}
              />
            </div>
          </div>
          {canCreate && (
          <div style={{ display: 'flex', gap: 18, justifyContent: 'center', padding: '20px 16px', borderBottom: '1px solid #eef0f3' }}>
                {QUICK_ACTIONS.map(({ type, label, Icon }) => (
                  <div key={type} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <button type="button" title={`Log a ${label}`} onClick={() => openLogActivity(type)} style={quickActionBtnStyle}>
                      <Icon size={15} />
                    </button>
                    <span style={quickActionLabelStyle}>{label}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <button type="button" title="Compose Email" onClick={openComposeEmail} style={quickActionBtnStyle}>
                    <Send size={15} />
                  </button>
                  <span style={quickActionLabelStyle}>Compose Email</span>
                </div>
              </div>
          )}
              <div style={{ display: 'flex', gap: 20, padding: '14px 16px 0', borderBottom: '1px solid #b3bbc7' }}>
                {ACTIVITY_TABS.map((label) => {
                  const isActive = activeActivityTab === label;
                  const count = activities.filter((a) => activityMatchesTab(a, label)).length;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setActiveActivityTab(label)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: isActive ? 700 : 400,
                        color: isActive ? '#222' : '#666',
                        paddingBottom: 12,
                        borderBottom: isActive ? '2px solid #000' : '2px solid transparent',
                      }}
                    >
                      {label}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                        background: isActive ? '#1976d2' : '#eceef1',
                        color: isActive ? '#fff' : '#666',
                      }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Scrolls inside its own card rather than growing the whole
                  page taller — same maxHeight/overflowY idiom as the left
                  column's card stack above, needed once an Entity's Email
                  Sync history runs past a screenful of rows. */}
              <div style={{ minHeight: 280, maxHeight: 'calc(100vh - 420px)', overflowY: 'auto', padding: '14px 16px' }}>
                {activitiesLoading ? (
                  <div style={{ textAlign: 'center', color: '#9aa5b5', paddingTop: 40 }}>Loading...</div>
                ) : (() => {
                  const filtered = activities.filter((a) => activityMatchesTab(a, activeActivityTab));
                  if (!filtered.length) {
                    return (
                      <div style={{ textAlign: 'center', color: '#9aa5b5', paddingTop: 40 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{activeActivityTab}</div>
                        <div style={{ fontSize: 13 }}>Nothing logged yet</div>
                      </div>
                    );
                  }
                  return filtered.map((a) => {
                    const Icon = ACTIVITY_TYPE_ICONS[a.activity_type];
                    // `contacts` is always the full "associated with" list
                    // (every Activity Type is one-to-many) — including for
                    // a rolled-up row (logged directly against a linked
                    // contact, not this Entity: see fetchActivities' rollup
                    // comment), since LogActivityDrawer pre-fills that
                    // contact into the list rather than leaving it implicit.
                    const contactName = a.contact_unknown ? 'Contact Unknown' : ((a.contacts || []).map((c) => c.name).join(', ') || null);
                    const associationNotes = (a.contacts || []).filter((c) => c.ActivityContact?.note);
                    // Reached here via org-rollup or a pure association (see
                    // listActivities) rather than owning this Activity
                    // directly — labeled "via {owner}" so the feed doesn't
                    // misread as a direct interaction with this Entity.
                    const viaOwner = (a.commercial_entity && String(a.commercial_entity.id) !== String(entity.id)) ? a.commercial_entity : null;
                    const isExpanded = expandedActivityId === a.id;
                    // The one row (if any) actually driving this entity's
                    // Pipeline dot (story #227 follow-up) — resolved
                    // server-side in pipelineDotResolver.js, not re-derived
                    // here, so this always agrees with the board.
                    const rowDot = entity?.pipeline_dot?.activity_id === a.id ? entity.pipeline_dot : null;
                    // Every Activity Type can spawn a follow-up Task (issue
                    // #218), a Task included — chaining a nudge off an
                    // earlier to-do is a legitimate use of the same action.
                    // A System Activity is the app's own record — read-only,
                    // so none of the row actions apply.
                    const isSystem = a.activity_type === SYSTEM_ACTIVITY_TYPE;
                    return (
                      <div key={a.id} style={{ ...childRowStyle, cursor: 'pointer', paddingRight: isSystem ? undefined : 36 }} onClick={() => setExpandedActivityId(isExpanded ? null : a.id)}>
                        {!isSystem && (
                          <>
                            <div onClick={(e) => e.stopPropagation()} style={activityMenuStyle}>
                              <RowActionsMenu
                                ariaLabel="Activity actions"
                                items={[
                                  { key: 'follow-up', label: 'Create Task', needs: 'create', onClick: () => openLogActivity('task', a) },
                                  { key: 'edit', label: 'Edit', onClick: () => openEditActivity(a) },
                                  { key: 'move', label: 'Move Activity', needs: 'update', onClick: () => setMovingActivity(a) },
                                  { key: 'delete', label: 'Delete', danger: true, onClick: () => deleteActivityRow(a.id) },
                                ]}
                              />
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid #d7dbe3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#5a6b85' }}>
                            <Icon size={14} />
                          </div>
                          {a.activity_type === 'task' && (
                            <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, marginTop: 6 }}>
                              <ToggleSwitch checked={!!a.is_done} onChange={(v) => toggleActivityDone(a, v)} disabled={!canUpdate} title={a.is_done ? 'Mark Open' : 'Mark Done'} />
                            </div>
                          )}
                          <div style={{ minWidth: 0, flex: 1, opacity: a.is_done ? 0.55 : 1 }}>
                            {a.topic && (
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 2, paddingRight: 84, textDecoration: a.is_done ? 'line-through' : 'none' }}>{a.topic}</div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              {isExpanded ? <ChevronDown size={13} color="#9aa5b5" /> : <ChevronRight size={13} color="#9aa5b5" />}
                              {rowDot && <TrafficLightDot state={rowDot.state} title={rowDot.title} detail={rowDot.detail} />}
                              <span style={{ fontSize: 13, fontWeight: a.topic ? 400 : 600, color: a.topic ? '#666' : '#222' }}>
                                {a.activity_type === 'task' && a.activity_date ? 'Due ' : ''}
                                {formatDate(a.activity_date) || '—'}
                                {a.start_time && ` · ${a.start_time.slice(0, 5)}${a.end_time ? `–${a.end_time.slice(0, 5)}` : ''}`}
                              </span>
                              {a.outcome && <CategoryPill value={a.outcome} />}
                              {a.activity_type === 'task' && a.assignee && <CategoryPill value={`Assigned: ${a.assignee.name}`} />}
                              {viaOwner && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#666' }}>
                                  via
                                  <RecordChip
                                    size="sm"
                                    label={viaOwner.name}
                                    href={`/commercial-entities/${viaOwner.id}`}
                                    icon={viaOwner.type === 'individual' ? UserRound : Building2}
                                    origin={{ label: entity.name, path: `/commercial-entities/${entity.id}` }}
                                    title={`Open ${viaOwner.name}`}
                                  />
                                </span>
                              )}
                              <span style={{ fontSize: 12, color: '#9aa5b5' }}>{a.source === 'email_sync'
                                  // A staff mailbox (issue #259) names its staff member; the shared mailbox has none.
                                  ? `Auto-logged via Email Sync${a.user?.name ? ` · ${a.user.name}'s mailbox` : ''}`
                                  : ['portal_download', 'portal_account', 'portal_order', 'portal_quote', 'portal_address'].includes(a.source) ? `Customer portal · ${a.email_from || '—'}`
                                  : a.source === 'portal_logins' ? 'Automatic' : `Logged by ${a.user?.name || '—'}`}</span>
                            </div>
                            {a.source === 'portal_address' && a.activity_type === 'task' && !a.is_done && a.requested_address && canUpdate && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 6 }}>
                                <button type="button" onClick={() => acceptAddressRequest(a)} title="Add this address to the Customer as a Ship To address" style={relationshipActionStyle}>
                                  Accept address
                                </button>
                              </div>
                            )}
                            {contactName && (
                              <div style={{ fontSize: 12, color: '#9aa5b5', marginTop: 2 }}>{contactName}</div>
                            )}
                            {(a.pricelist_sends || []).map((ps) => (
                              <div key={ps.id} style={{ fontSize: 12, color: '#5a6b85', marginTop: 2 }}>
                                <strong>Pricelist:</strong> {ps.pricelist_name} ({pricelistScopeLabel(ps)})
                                {a.source === 'portal_download'
                                  ? ` · ${ps.format === 'extended' ? 'Extended' : 'Compact'} PDF downloaded ${formatDate(ps.sent_on)}`
                                  : ` · sent ${formatDate(ps.sent_on)}`}
                              </div>
                            ))}
                            {/* The Quote this email carried, or that was
                                marked sent (issue #332). */}
                            {a.quote && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#5a6b85', marginTop: 2 }}>
                                <strong>Quote:</strong>
                                <RecordChip
                                  size="sm"
                                  label={quoteNumberOf(a.quote)}
                                  href={`/quotes/${a.quote.id}`}
                                  icon={FileText}
                                  origin={{ label: entity.name, path: `/commercial-entities/${entity.id}` }}
                                  title={`Open Quote ${quoteNumberOf(a.quote)}`}
                                />
                              </div>
                            )}
                            {/* Attachment Library files sent with this email —
                                the Pricelist is already shown above. */}
                            {(a.attachments || []).filter((att) => att.source === 'media_image').map((att) => (
                              <div key={att.media_image_id} style={{ fontSize: 12, color: '#5a6b85', marginTop: 2 }}>
                                <strong>Attachment:</strong> {att.filename}
                              </div>
                            ))}
                            {isExpanded ? (
                              <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: '#5a6b85', marginBottom: 6 }}>
                                  {a.direction && <span><strong>Direction:</strong> {DIRECTION_LABELS[a.direction] || a.direction}</span>}
                                  {a.channel && <span><strong>Channel:</strong> {CHANNEL_LABELS[a.channel] || a.channel}</span>}
                                  {a.email_from && <span><strong>From:</strong> {a.email_from}</span>}
                                  {a.email_to && <span><strong>To:</strong> {a.email_to}</span>}
                                </div>
                                {/* Move trail (activityController.moveActivity)
                                    — a visible correction, not a silent
                                    rewrite, when this Activity's owner has
                                    ever been reassigned. */}
                                {a.moved_from_entity && (
                                  <div style={{ fontSize: 12, color: '#5a6b85', marginBottom: 6 }}>
                                    <strong>Moved from:</strong> {a.moved_from_entity.name} ({formatDate(a.moved_at)}{a.moved_by_user ? ` by ${a.moved_by_user.name}` : ''})
                                  </div>
                                )}
                                {/* Both halves of the Task↔Activity back-link
                                    (issue #218's AC) — a Task shows the
                                    Activity it followed from, any Activity
                                    (including another Task) shows the Task(s)
                                    that followed from it. */}
                                {a.followed_from_activity && (
                                  <div
                                    onClick={() => { setActiveActivityTab('All activities'); setExpandedActivityId(a.followed_from_activity.id); }}
                                    style={{ fontSize: 12, color: '#5a6b85', marginBottom: 6, cursor: 'pointer' }}
                                  >
                                    <strong>Followed up on:</strong> {ACTIVITY_TYPE_LABELS[a.followed_from_activity.activity_type] || a.followed_from_activity.activity_type}
                                    {a.followed_from_activity.topic ? ` — ${a.followed_from_activity.topic}` : ''}
                                    {' '}({formatDate(a.followed_from_activity.activity_date) || '—'})
                                  </div>
                                )}
                                {(a.follow_up_tasks || []).length > 0 && (
                                  <div style={{ fontSize: 12, color: '#5a6b85', marginBottom: 6 }}>
                                    <strong>Follow-up Task{a.follow_up_tasks.length > 1 ? 's' : ''}:</strong>{' '}
                                    {a.follow_up_tasks.map((t, i) => (
                                      <span key={t.id}>
                                        {i > 0 && ', '}
                                        <span onClick={() => { setActiveActivityTab('All activities'); setExpandedActivityId(t.id); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                          {t.topic || `Task #${t.id}`}
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {associationNotes.length > 0 && (
                                  <div style={{ marginBottom: 6 }}>
                                    {associationNotes.map((c) => (
                                      <div key={c.id} style={{ fontSize: 12, color: '#5a6b85' }}><strong>{c.name}:</strong> {c.ActivityContact.note}</div>
                                    ))}
                                  </div>
                                )}
                                {a.notes && (
                                  <RichText html={a.notes} style={{ fontSize: 13, color: '#444' }} />
                                )}
                              </div>
                            ) : a.notes && (
                              <div
                                style={{ fontSize: 13, color: '#444', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', whiteSpace: 'pre-line' }}
                              >
                                {a.source === 'email_sync' ? cleanEmailPreviewText(htmlToPreviewText(a.notes)) : htmlToPreviewText(a.notes)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
        </div>

        {/* Right — linked records, grouped. Same standalone-card-per-
            Accordion treatment as the left column above. */}
        <div style={{ minWidth: 0 }}>
          {(showsCard('people') || people.length > 0) && (
          <Accordion
            title={`People (${people.filter((r) => !r.ended_at).length})`}
            defaultOpen={false}
            menuItems={[
              { key: 'add-person', label: 'Add Person', onClick: () => setAddLinkCategory('individual') },
            ]}
          >
            <LinkRows
              items={people}
              emptyLabel="No People yet"
              navigate={navigate}
              onRemove={removeLink}
              onEnd={endLink}
              onUndoEnd={undoEndLink}
              onEditEndedAt={editLinkEndedAt}
              linkRoleTypes={linkRoleTypes}
              onEditRole={patchLinkRole}
            />
          </Accordion>
          )}
          <AccountApplicationsCard
            entityId={id}
            entity={entity}
            isLifecycle={isCustomerEntity(entity)}
            onEntityChanged={({ activitiesOnly }) => {
              if (!activitiesOnly) fetchEntity();
              fetchActivities();
            }}
          />
          <CustomerLoginsCard
            entityId={id}
            entity={entity}
            isCustomer={(entity.relationships || []).some((r) => r.relationship_type?.system_key === 'customer')}
          />
          <CustomerQuickBooksCard
            entityId={id}
            isCustomer={(entity.relationships || []).some((r) => r.relationship_type?.system_key === 'customer')}
          />
          {(showsCard('companies') || companies.length > 0) && (
          <Accordion
            title={`Companies (${companies.filter((r) => !r.ended_at).length})`}
            defaultOpen={false}
            menuItems={[
              { key: 'add-company', label: 'Add Company', onClick: () => setAddLinkCategory('organization') },
            ]}
          >
            <LinkRows
              items={companies}
              emptyLabel="No Companies yet"
              navigate={navigate}
              onRemove={removeLink}
              onEnd={endLink}
              onUndoEnd={undoEndLink}
              onEditEndedAt={editLinkEndedAt}
              linkRoleTypes={linkRoleTypes}
              onEditRole={patchLinkRole}
            />
          </Accordion>
          )}
          {isOrganization && <OwnedBrandsCard entity={entity} />}
          {(showsCard('trade_shows') || tradeShowAttendances.length > 0) && (
          <Accordion
            title={`Trade Shows (${tradeShowAttendances.length})`}
            defaultOpen={false}
            menuItems={[
              { key: 'add-attendance', label: 'Add Attendance', onClick: () => setShowAddAttendance(true) },
            ]}
          >
            {tradeShowAttendances.length ? tradeShowAttendances.map((a) => {
              const show = a.trade_show;
              const label = show ? `${show.series?.name || ''} ${show.year}` : 'Trade Show';
              return (
                <div key={a.id} style={childRowStyle}>
                  <>{canUpdate && (<button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeTradeShowAttendance(a.id, label); }}
                    title="Remove"
                    style={removeRowBtnStyle}
                  >×</button>)}</>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1976d2' }}>{label}</div>
                    <CategoryPill value={a.role?.name} />
                  </div>
                  {show?.location && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{show.location}</div>}
                  {a.notes && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{a.notes}</div>}
                </div>
              );
            }) : <div style={emptyNoteStyle}>No Trade Show Attendance yet</div>}
          </Accordion>
          )}
          {(showsCard('quotes') || quotes.length > 0) && (
          <Accordion
            title={`Quotes (${quotes.length})`}
            defaultOpen={false}
            menuItems={canQuote ? [
              { key: 'create-quote', label: 'Create Quote', onClick: () => navigate('/quotes/new', { state: { customer: entity } }) },
            ] : undefined}
          >
            {quotes.length ? quotes.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => navigate(`/quotes/${q.id}`)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f0f2f5', padding: '6px 0', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1976d2' }}>{q.display_number}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{formatPrice(q.total_inc_gst ?? q.total_amount)}</div>
                </div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {formatDate(q.quote_date)} · {q.converted_sales_order ? 'Converted' : (QUOTE_STATUS_LABELS[q.status] || q.status)}
                </div>
              </button>
            )) : <div style={emptyNoteStyle}>No Quotes yet</div>}
          </Accordion>
          )}
          {(showsCard('sales_orders') || salesOrders.length > 0) && (
          <Accordion
            title={`Sales Orders (${salesOrders.length})`}
            defaultOpen={false}
            menuItems={canQuote ? [
              { key: 'create-sales-order', label: 'Create Sales Order', onClick: () => navigate('/sales-orders/new', { state: { customer: entity } }) },
            ] : undefined}
          >
            {salesOrders.length ? salesOrders.map((so) => {
              // Shipped something but not everything — the same rule
              // salesOrderController.listSalesOrders' backorder=true filter
              // uses. Surfaced here so a customer's stalled orders are
              // visible without a separate report screen.
              const linesTotal = Number(so.lines_qty_total || 0);
              const linesShipped = Number(so.lines_qty_shipped_total || 0);
              const isBackorder = linesTotal > 0 && linesShipped > 0 && linesShipped < linesTotal;
              return (
                <button
                  key={so.id}
                  type="button"
                  onClick={() => navigate(`/sales-orders/${so.id}`)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f0f2f5', padding: '6px 0', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1976d2' }}>{so.display_number}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{formatPrice(so.total_inc_gst ?? so.total_amount)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {formatDate(so.order_date)} · {SALES_ORDER_STATUS_LABELS[so.status] || so.status}
                    {isBackorder && <span style={{ marginLeft: 6, color: '#c98a1f', fontWeight: 600 }}>· Backorder</span>}
                  </div>
                </button>
              );
            }) : <div style={emptyNoteStyle}>No Sales Orders yet</div>}
          </Accordion>
          )}
        </div>
      </div>
      </div>

      <BrowseNav id={id} ids={browseIds} buildPath={(targetId) => `/commercial-entities/${targetId}`} noun="records" extraState={location.state?.origin ? { origin: location.state.origin } : undefined} />

      <AddLinkDrawer
        open={!!addLinkCategory}
        onOpenChange={(open) => { if (!open) setAddLinkCategory(null); }}
        category={addLinkCategory}
        ownerEntityId={id}
        ownerEntityType={entity?.type}
        ownerEntityName={entity?.name}
        onAdded={(updated) => { setEntity(updated); setAddLinkCategory(null); }}
      />
      <AddTradeShowAttendanceDrawer
        open={showAddAttendance}
        onOpenChange={setShowAddAttendance}
        ownerEntityId={id}
        onAdded={(updated) => { setEntity(updated); setShowAddAttendance(false); }}
      />
      <LogActivityDrawer
        open={logDrawerOpen}
        onOpenChange={setLogDrawerOpen}
        activityType={drawerActivityType}
        entity={editingActivityEntity || entity}
        contactOptions={contactOptions}
        editingActivity={editingActivity}
        followUpFrom={followUpFromActivity}
        onSaved={handleActivitySaved}
        onFollowUp={(saved) => openLogActivity('task', saved)}
      />
      {pullEmailsOpen && (
        <PullEmailsDialog entityId={entity.id} onOpenChange={setPullEmailsOpen} onPulled={fetchActivities} />
      )}
      <ComposeEmailDrawer
        open={composeEmailOpen}
        onOpenChange={setComposeEmailOpen}
        entity={entity}
        onSaved={handleActivitySaved}
      />
      <MoveActivityDrawer
        open={!!movingActivity}
        activity={movingActivity}
        onOpenChange={(o) => { if (!o) setMovingActivity(null); }}
        onMoved={handleActivityMoved}
      />
    </div>
  );
}

export default CommercialEntityDetailPage;
